import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import api, { extractErrorMessage } from '@app/services/api';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useUserSearch, useFields, useMentionSuggestions } from '@platform/query';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { formatDate, useDateFormatVersion, formatIfCanonicalDate } from '@platform/app-shell/frontend/utils/dateFormat';
import RichTextEditor from '@modules/database/work-items/frontend/editors/RichTextEditor';
import { replyDraft } from './replyDraft';
import { readDraft, writeDraft, clearDraft, hasDraft } from './unsavedDrafts';
import WorkItemKey from '@modules/database/work-items/frontend/components/WorkItemKey';
import FieldEditor from '@modules/database/work-items/frontend/editors/FieldEditor';
import supportDeskApi from '@modules/support-desk/frontend/api';
import UserEditor from '@modules/database/work-items/frontend/editors/UserEditor';
import DateEditor from '@modules/database/work-items/frontend/editors/DateEditor';
import MultiSelectEditor from '@modules/database/work-items/frontend/editors/MultiSelectEditor';
import MultiUserEditor from '@modules/database/work-items/frontend/editors/MultiUserEditor';
import CascadingSelectEditor from '@modules/database/work-items/frontend/editors/CascadingSelectEditor';
import { cascadingLabel, cascadingPayload } from '@modules/database/work-items/frontend/utils/cascadingSelect';
import { adfToTipTap, tipTapToAdf, preprocessAdfWikiMarkup } from '@modules/database/work-items/frontend/utils/adfConverter';
import resolveImageRefs from '@modules/database/work-items/frontend/utils/resolveImageRefs';
import { contentFingerprint } from '@modules/database/work-items/frontend/utils/contentKey';
import { isFieldEditable, getEditorType, getRawFieldValue } from '@modules/database/work-items/frontend/utils/fieldConfig';
import { getFieldValue } from '@modules/database/work-items/frontend/utils/fieldUtils';
import { formatNumberDisplay } from '@modules/database/work-items/frontend/utils/fieldFormatter';
import { getSchemaEditorType, resolveScreenEditorType } from '@modules/database/work-items/frontend/utils/screenEditorType';
import { issueResultsHref, zebraSearchHref, handleIssueLinkActivation } from '@modules/zebra-search/frontend/utils/issueLinks';
import BulkChangeModal, { useBulkAccess } from '@modules/database/work-items/frontend/components/BulkChangeModal';
import { evaluateFormula } from '@modules/folders/frontend/utils/formulaEngine';
import { evalColumnFormula } from '@modules/folders/frontend/utils/expr/index';
import CreateIssueModal from '@modules/database/work-items/frontend/components/CreateIssueModal';
import LineItemsField from '@modules/database/line-items/frontend/LineItemsField';
import WhiteboardField from '@modules/database/whiteboard/frontend/WhiteboardField';
// A Document field's paper (and its PDF) loads only when a work item shows one.
const DocumentField = lazy(() => import('@modules/database/document-field/frontend/DocumentField'));
import WorkItemRefField from '@modules/database/work-items/frontend/components/WorkItemRefField/WorkItemRefField';
import { fieldViewFor, fieldViewOptions } from '@modules/database/work-items/frontend/components/FieldView/fieldViews';
import FieldViewHost, { FieldViewSection } from '@modules/database/work-items/frontend/components/FieldView/FieldViewHost';
import TransitionScreenModal from '@modules/database/work-items/frontend/components/TransitionScreenModal';
import ConfirmDialog from '@platform/ui/ConfirmDialog';
import SearchableSelect from '@platform/ui/SearchableSelect';
import UserAvatar from '@platform/ui/UserAvatar';
import IssuePickerInput from '@modules/database/work-items/frontend/components/IssuePickerInput';
import { RelatedSection, RelatedTable, RelatedColumnPicker } from '@modules/database/work-items/frontend/components/RelatedItems';
import { RELATED_SECTIONS, readRelatedColumnIds, writeRelatedColumnIds, toggleRelatedColumnId, relatedFieldCatalogue, relatedColumnsFromIds } from '@modules/database/work-items/frontend/components/RelatedItems/relatedColumns';
import ExportReportButton from '@modules/reports/frontend/components/ExportReportButton';
import ParentEditor from '@modules/database/work-items/frontend/editors/ParentEditor';
import AttachmentPreview, { AttachmentThumb } from '@modules/database/work-items/frontend/components/AttachmentPreview';
import { LINK_TYPE_NAME } from '@client/config/client.config';
import { socket } from '@platform/http/socket';
import { getValidParentTypes, getRelationshipForChild, getRelationshipsForChild, getAllowedChildTypes, getAllowedParentRelationships, extractAllEdgesFromTree, resolveHierarchyCreateOptions } from '@modules/folders/frontend/utils/treeHelpers';
import { combineJqlFilters, combineExpressions } from '@modules/folders/frontend/utils/edgeFilters';
import '@modules/database/work-items/frontend/components/IssueScreenPanel/IssueScreenPanel.css';
import { isContentField, hasLayoutSections as layoutHasSections, bodyTabIndexes, sidebarFields, showDetailsPanel } from '@modules/database/work-items/frontend/components/IssueScreenPanel/screenSections';

// The Architectural Hierarchy tree per project, resolved once per session by
// the panel itself when its host passes no `creationTree` (Kanban opens the
// panel without one; Structure and the search results pass theirs). null =
// the project's scheme has no tree.
const hierarchyTreeByProject = new Map();
// The project's live work types, for the "+ Create" menu (tree ∩ project).
const issueTypesByProject = new Map();

// A just-linked work item as the Linked Work Items rows read it, from the
// picker's suggestion (key, summary, type icon, status). Missing pieces stay
// blank rather than faked — the next read of the issue fills them.
const STATUS_CATEGORY_COLOR = { new: 'blue-gray', indeterminate: 'yellow', done: 'green' };
function pickedIssueStub(key, picked) {
  return {
    key,
    fields: {
      summary: picked?.summary || '',
      status: picked?.statusName
        ? { name: picked.statusName, statusCategory: { key: picked.statusCategoryKey || undefined, colorName: STATUS_CATEGORY_COLOR[picked.statusCategoryKey] || 'blue-gray' } }
        : null,
      issuetype: { name: picked?.issueTypeName || undefined, iconUrl: picked?.iconUrl || undefined },
    },
  };
}

// The "+" link picker's relationships. The tree holds one edge per work
// type, so a type list of fourteen reads as fourteen choices of the same
// link; folded by what the link will actually BE (role + sub-task / child /
// link type + side), each relationship is one choice listing its types.
// The label is the phrase THIS work item will show — the tree's edge
// direction is the side the CHILD shows, so a parent shows the other one.
// Edges of one relationship create the identical link whatever the
// candidate's type; only their filters differ, ORed per type below.
function groupRelationshipOptions(options, linkTypes, defaultLinkTypeName) {
  const groups = new Map();
  for (const opt of options) {
    const e = opt.edge || {};
    const rel = e.relationshipType || 'linked';
    const linkTypeName = rel === 'linked' ? (e.linkTypeName || defaultLinkTypeName) : '';
    const dir = rel === 'linked' ? (e.linkDirection || 'outward') : '';
    const key = `${opt.role}|${rel}|${linkTypeName.toLowerCase()}|${dir}`;
    if (!groups.has(key)) {
      let label;
      if (rel === 'subtask') label = opt.role === 'child' ? 'Sub-task' : 'Parent (this is its sub-task)';
      else if (rel === 'child') label = opt.role === 'child' ? 'Child work item' : 'Parent (this is its child)';
      else {
        const lt = linkTypes?.find(l => (l.name || '').toLowerCase() === linkTypeName.toLowerCase());
        const shownDir = opt.role === 'child' ? (dir === 'inward' ? 'outward' : 'inward') : dir;
        label = lt ? (lt[shownDir] || `${linkTypeName} : ${shownDir}`) : `${linkTypeName} : ${shownDir}`;
      }
      groups.set(key, { key, role: opt.role, relationshipType: rel, linkTypeName, label, options: [] });
    }
    const g = groups.get(key);
    if (!g.options.some(o => o.candidateTypeName === opt.candidateTypeName)) g.options.push(opt);
  }
  for (const g of groups.values()) {
    g.typeNames = g.options.map(o => o.candidateTypeName).filter(Boolean);
    // One type's filters as they are; several types each keep their own.
    const perType = g.options.map(o => ({ name: o.candidateTypeName, jql: combineJqlFilters(o.edge?.filters) }));
    g.jql = perType.length === 1 ? perType[0].jql
      : perType.some(t => t.jql)
        ? perType.map(t => `(issuetype = "${String(t.name).replace(/"/g, '\\"')}"${t.jql ? ` AND ${t.jql}` : ''})`).join(' OR ')
        : '';
    g.expression = g.options.length === 1 ? combineExpressions(g.options[0].edge?.filters) : '';
  }
  return [...groups.values()];
}

// The relationship member a picked work item belongs to — by its type, else
// the first (every member makes the same link).
function optionForPicked(group, picked) {
  const typeName = String(picked?.issueTypeName || '').toLowerCase();
  return group.options.find(o => String(o.candidateTypeName || '').toLowerCase() === typeName) || group.options[0];
}

// The permission keys this panel gates its controls on. The screen batch
// asks for the same list server-side (work-item-screen-handler
// PANEL_PERMISSION_KEYS); this copy is the fallback round-trip's.
const PANEL_PERMISSION_KEYS = 'DELETE_ISSUES,CREATE_ATTACHMENTS,DELETE_OWN_ATTACHMENTS,DELETE_ALL_ATTACHMENTS,SET_ISSUE_SECURITY';
const readAttachmentPerms = (perms) => ({
  create: !!perms.CREATE_ATTACHMENTS?.havePermission,
  deleteOwn: !!perms.DELETE_OWN_ATTACHMENTS?.havePermission,
  deleteAll: !!perms.DELETE_ALL_ATTACHMENTS?.havePermission,
});

// Display-name overrides — SYSTEM keys only, whose names are universal. Never a customfield id:
// those ids are tenant data (customfield_10102 was the archive tenant's Story Points, but a
// greenfield tenant's 10102 is whatever was created natively — it rendered a client's "Duration"
// as "Story Points"). The field's own registry name always wins for custom fields.
const FIELD_NAME_OVERRIDES = {
  timeoriginalestimate: 'Original Estimate',
  timeestimate: 'Remaining Estimate',
  timespent: 'Time Spent',
};

// Fields to skip — system/non-editable or rendered as dedicated sections
const SKIP_FIELDS = new Set([
  'attachment', 'worklog', 'comment', 'thumbnail',
  'statuscategorychangedate',
  'aggregatetimespent', 'aggregatetimeoriginalestimate',
  'aggregatetimeestimate', 'aggregateprogress', 'progress',
  'security',
  // Rendered as dedicated sections, not inline fields:
  'issuelinks', 'subtasks', 'timetracking',
  'watches', 'votes',
  // Already the panel's title (h2.screen-panel-summary) — showing it again as
  // a field printed the same text twice on every screen. Double-click the
  // title to edit it.
  'summary',
  // Jira's own screens carry Issue Type as their first field, and Jira's
  // issue view never draws it in the body: the type is the icon and name in
  // the header. Stride's header carries the same icon, so the body row was
  // the same text twice (2026-09-11, a client's Project work items).
  'issuetype',
]);

// Fields that go to the Details sidebar when we have to synthesise a layout
// (see defaultSectionType) — Jira's own default "Details" panel, near enough.
const DEFAULT_DETAILS_FIELDS = new Set([
  'assignee', 'reporter', 'creator', 'priority', 'labels', 'duedate',
  'components', 'fixVersions', 'versions', 'resolution',
  'parent', 'project', 'issuetype', 'created', 'updated',
]);

// Column tracks for the child / linked-item mini-tables.
//
// Every column used to be laid out at exactly the width the folder configured,
// which is right for a date, a key or a status chip — they are all the size
// they are going to be. It is wrong for SUMMARY, the one column holding a
// sentence: a child called "Project management, supervision and site
// establishment" was clipped to "Project management, su…" while empty panel
// sat to its right, because a fixed track cannot take up slack.
//
// Summary alone gets a flexible track — but a BOUNDED one. Letting it take
// every spare pixel (it had 1.6fr) fixed the clipping and broke the rhythm:
// on a wide panel summary swallowed the slack and drove Status and the dates
// out to the far edge, leaving a gulf through the middle of the row. Between
// the two bounds it takes what it needs; past MINI_SUMMARY_MAX the leftover
// simply trails off the end of the row, where empty space is unremarkable.
// Below the minimum the table scrolls sideways as it always did, so nothing
// is ever squeezed to nothing.
const MINI_SUMMARY_MIN = 220;
const MINI_SUMMARY_MAX = 420;
function miniColumnTracks(columns) {
  return (columns || [])
    .map((c) => (c.fieldId === 'summary'
      ? `minmax(${Math.max(c.width || 120, MINI_SUMMARY_MIN)}px, ${MINI_SUMMARY_MAX}px)`
      : `${c.width || 120}px`))
    .join(' ');
}

// Section a field lands in when NO Jira issue layout can be resolved at all —
// not for this project, not from another project sharing the same view screen,
// not from the same work type anywhere (see issue-layout-handler.js).
//
// `null` means "unplaced", which renders in the MAIN column — the same thing a
// real Jira layout does with every screen field it doesn't name. That default
// matters: the archive client's cached layouts place only ~23 context fields and leave the
// rest unplaced, so main-column-by-default is what parity actually looks like.
// The first cut of this had the rule the other way round (rich-text to the
// main column, EVERYTHING else to Details), which emptied whole screen tabs
// into one enormous sidebar.
const defaultSectionType = (field) =>
  (DEFAULT_DETAILS_FIELDS.has(field.key) ? 'primary' : null);

// Empty field placeholder for screen fields
const EMPTY_FIELD = <span className="screen-field-empty">None</span>;

// Format a field value for display
function formatDisplayValue(fieldKey, value, editorType, { adminMode = false, onIssueKeyClick = null } = {}) {
  if (value === null || value === undefined) return EMPTY_FIELD;

  if (fieldKey === 'status') return value?.name || EMPTY_FIELD;
  if (fieldKey === 'priority') return value?.name || EMPTY_FIELD;
  if (fieldKey === 'resolution') return value?.name || 'Unresolved';
  if (fieldKey === 'issuetype') return value?.name || EMPTY_FIELD;
  if (fieldKey === 'project') return value?.name || EMPTY_FIELD;
  if (fieldKey === 'parent') {
    if (!value?.key) return EMPTY_FIELD;
    // The parent as a work item reads: its type icon, its key, its summary.
    const pf = value.fields || {};
    return (
      <span className="screen-parent-value">
        {pf.issuetype?.iconUrl && (
          <img src={api.getJiraImageUrl(pf.issuetype.iconUrl)} alt="" title={pf.issuetype.name || ''} width="16" height="16" />
        )}
        <a
          className="linked-issue-key-link"
          href={issueResultsHref(value.key)}
          onClick={(e) => handleIssueLinkActivation(e, onIssueKeyClick, value.key)}
        >
          {value.key}
        </a>
        {pf.summary && <span className="screen-parent-value-summary" title={pf.summary}>{pf.summary}</span>}
      </span>
    );
  }

  if (editorType === 'user-display' || editorType === 'multiuser-display') {
    // Jira's user chip: a 24px round avatar and the name. UserAvatar gives
    // the same coloured-initials fallback the comment stream uses, so a
    // person without a cached picture still reads as a person, not a gap.
    const renderUser = (u, i) => (
      <span key={u.accountId || u.name || i} className="user-display-chip">
        <UserAvatar user={u} size={24} resolveImageUrl={api.getJiraImageUrl} />
        <span className="user-name">{u.displayName || u.name || u.emailAddress || ''}</span>
      </span>
    );
    if (Array.isArray(value)) {
      const users = value.filter(u => u != null);
      if (users.length === 0) return EMPTY_FIELD;
      return <span className="user-display-cell">{users.map(renderUser)}</span>;
    }
    if (!value) return EMPTY_FIELD;
    return renderUser(value, 0);
  }
  if (editorType === 'labels-display') {
    return Array.isArray(value) && value.length > 0 ? value.filter(Boolean).join(', ') : EMPTY_FIELD;
  }
  if (editorType === 'versions-display' || editorType === 'components-display' || editorType === 'multiselect-display') {
    if (Array.isArray(value)) {
      const names = value.filter(v => v != null).map(v => v.name || v.value || v).filter(Boolean);
      return names.length > 0 ? names.join(', ') : EMPTY_FIELD;
    }
    return EMPTY_FIELD;
  }
  if (editorType === 'select') {
    return value?.name || value?.value || EMPTY_FIELD;
  }
  // "Toyota - Hilux": the option, then the sub-option (utils/cascadingSelect.js).
  if (editorType === 'cascading-select') return cascadingLabel(value) || EMPTY_FIELD;
  if (editorType === 'time-display' || editorType === 'time-readonly') {
    if (!value && value !== 0) return EMPTY_FIELD;
    // Value is in seconds from Jira; could be a temp string after inline edit
    if (typeof value === 'number') {
      const totalMinutes = Math.floor(value / 60);
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
      if (hours > 0) return `${hours}h`;
      if (mins > 0) return `${mins}m`;
      return '0m';
    }
    return String(value);
  }
  if (editorType === 'datetime-display') {
    if (!value) return EMPTY_FIELD;
    return formatDate(value, 'datetime', EMPTY_FIELD);
  }
  if (editorType === 'date') {
    if (!value) return EMPTY_FIELD;
    return formatDate(value, 'date', EMPTY_FIELD);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  // Number fields read as Jira shows them: "41,943.2", not "41943.2". The
  // editor still opens on the raw value (editValue is seeded from the
  // field, not from this string).
  if (editorType === 'number') return formatNumberDisplay(value);
  return String(value);
}

// Format API payload for field update
function formatFieldForApi(fieldKey, value, editorType) {
  switch (fieldKey) {
    case 'summary': return { summary: value || '' };
    case 'description': return { description: value };
    case 'duedate': return { duedate: value || null };
    case 'priority': return { priority: value ? { id: value.id } : null };
    case 'resolution': return { resolution: value ? { id: value.id } : null };
    case 'assignee': return { assignee: value ? { accountId: value.accountId } : null };
    case 'reporter': return { reporter: value ? { accountId: value.accountId } : null };
    case 'labels': return { labels: Array.isArray(value) ? value : [] };
    case 'timeoriginalestimate': return { timetracking: { originalEstimate: value || '0m' } };
    case 'timeestimate': return { timetracking: { remainingEstimate: value || '0m' } };
    default:
      if (fieldKey?.startsWith('customfield_')) {
        if (editorType === 'select') return { [fieldKey]: value ? { id: value.id } : null };
        if (editorType === 'cascading-select') return { [fieldKey]: cascadingPayload(value) };
        if (editorType === 'number') return { [fieldKey]: value === '' ? null : Number(value) };
        if (editorType === 'user-display') return { [fieldKey]: value ? { accountId: value.accountId } : null };
        // Several people: the account ids, in order; nobody left clears the field.
        if (editorType === 'multiuser-display') {
          const people = (Array.isArray(value) ? value : []).filter(u => u && u.accountId);
          return { [fieldKey]: people.length ? people.map(u => ({ accountId: u.accountId })) : null };
        }
        if (editorType === 'multiselect-display') return { [fieldKey]: Array.isArray(value) ? value.map(v => ({ id: v.id })) : [] };
        if (editorType === 'date') {
          // Jira rejects '' on a date field with "must be of the format
          // yyyy-MM-dd"; only null clears it. Covers any customfield that
          // getSchemaEditorType classifies as 'date' (including date-flavoured
          // customs whose schema.type is 'string' — Plans / Advanced
          // Roadmaps' jpo-custom-field-baseline-start/end).
          return { [fieldKey]: value || null };
        }
        // Safety net for unknown customfields: send null on empty string.
        // Jira accepts null to clear any field; '' is rejected by some
        // (e.g. date-flavoured customfields whose schema we misclassified).
        // Both forms clear text customfields, so this is universally safe.
        return { [fieldKey]: value === '' ? null : value };
      }
      return { [fieldKey]: value };
  }
}

// Read-only editor types
const READONLY_TYPES = new Set([
  'status-badge', 'readonly-display', 'datetime-display',
  'labels-display', 'versions-display', 'components-display',
  'time-readonly',
  // A Work item reference is set by automations: never opens an editor.
  'workitemref',
  // A Document field draws its work item; it holds no value to edit.
  'document',
]);

// Format file size for display
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + units[i];
}

// Get file icon emoji by extension
function getFileIcon(filename, size = 40) {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  const h = Math.round(size * 1.25);
  let color, label;
  switch (ext) {
    case 'xls': case 'xlsx': case 'xlsm': case 'csv':
      color = '#1D6F42'; label = 'XLS'; break;
    case 'doc': case 'docx':
      color = '#2B579A'; label = 'DOC'; break;
    case 'pdf':
      color = '#DC3545'; label = 'PDF'; break;
    case 'ppt': case 'pptx':
      color = '#D24726'; label = 'PPT'; break;
    case 'zip': case 'rar': case '7z': case 'tar': case 'gz':
      color = '#8B6914'; label = 'ZIP'; break;
    case 'txt': case 'log': case 'md': case 'rtf':
      color = '#6C757D'; label = 'TXT'; break;
    case 'dwg': case 'dxf':
      color = '#0696D7'; label = 'CAD'; break;
    case 'stp': case 'step': case 'igs': case 'iges': case 'prt': case 'stl':
      color = '#00897B'; label = '3D'; break;
    case 'mp4': case 'avi': case 'mov': case 'wmv': case 'mkv':
      color = '#7B1FA2'; label = 'VID'; break;
    case 'mp3': case 'wav': case 'flac': case 'aac': case 'ogg':
      color = '#E91E63'; label = 'AUD'; break;
    case 'png': case 'jpg': case 'jpeg': case 'gif': case 'svg': case 'webp': case 'bmp':
      color = '#0288D1'; label = 'IMG'; break;
    case 'js': case 'ts': case 'jsx': case 'tsx': case 'py': case 'java': case 'rb':
      color = '#455A64'; label = 'CODE'; break;
    case 'json': case 'xml': case 'yaml': case 'yml':
      color = '#546E7A'; label = 'DATA'; break;
    default:
      color = '#6C757D'; label = ext?.toUpperCase()?.slice(0, 4) || 'FILE'; break;
  }
  return (
    <svg width={size} height={h} viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 4C2 1.79 3.79 0 6 0H20L30 10V36C30 38.21 28.21 40 26 40H6C3.79 40 2 38.21 2 36V4Z" fill="#F8F9FA" stroke="#DEE2E6" strokeWidth="1"/>
      <path d="M20 0L30 10H24C21.79 10 20 8.21 20 6V0Z" fill="#DEE2E6"/>
      <rect x="2" y="23" width="28" height="13" fill={color} opacity="0.9"/>
      <text x="16" y="33.5" textAnchor="middle" fill="white" fontSize="8" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="700">{label}</text>
    </svg>
  );
}

// Check if file is an image
function isImageFile(filename) {
  if (!filename) return false;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'].includes(ext);
}

// Strip every media node referencing the given attachment from an ADF
// document. Used by handleDeleteAttachment when the user deletes a
// file from the attachments panel — any wiki/rich-text field that
// referenced that file gets the corresponding mediaSingle/mediaGroup
// node removed so the field doesn't keep rendering a now-broken tile.
//
// Match strategy: we accept BOTH the Jira media UUID
// (attachment.mediaApiFileId, set as `attrs.id` on ADF media nodes)
// and the numeric attachment id, since legacy or hand-edited content
// occasionally stores the latter.
//
// Returns { doc, changed }. `changed` is true if anything was removed.
// The caller saves the cleaned doc only when `changed` is true.
function stripMediaFromAdf(doc, mediaUuid, attachmentIdStr) {
  if (!doc || typeof doc !== 'object') return { doc, changed: false };

  let changed = false;
  const targetIds = new Set();
  if (mediaUuid) targetIds.add(String(mediaUuid));
  if (attachmentIdStr) targetIds.add(String(attachmentIdStr));
  if (targetIds.size === 0) return { doc, changed: false };

  function clean(node) {
    if (!node || typeof node !== 'object') return node;

    // Direct media node match → signal removal to the parent.
    if (node.type === 'media' && node.attrs?.id != null
        && targetIds.has(String(node.attrs.id))) {
      return null;
    }

    if (!Array.isArray(node.content)) return node;

    const newContent = [];
    for (const child of node.content) {
      const cleaned = clean(child);
      if (cleaned == null) { changed = true; continue; }
      // mediaSingle/mediaGroup with empty content after cleaning →
      // drop the wrapper too, otherwise Jira rejects the doc.
      if ((cleaned.type === 'mediaSingle' || cleaned.type === 'mediaGroup')
          && (!cleaned.content || cleaned.content.length === 0)) {
        changed = true;
        continue;
      }
      newContent.push(cleaned);
    }
    return { ...node, content: newContent };
  }

  let cleanedDoc = clean(doc);
  // Jira rejects docs whose top-level content is empty — replace with
  // an empty paragraph so the save still succeeds.
  if (cleanedDoc && Array.isArray(cleanedDoc.content) && cleanedDoc.content.length === 0) {
    cleanedDoc = { ...cleanedDoc, content: [{ type: 'paragraph' }] };
  }
  return { doc: cleanedDoc, changed };
}

// resolveImageRefs (wiki-image:// + jira-media:// → attachment-content proxy
// URLs) moved to utils/resolveImageRefs.js so TransitionScreenModal can share
// it. It also matches numeric Stride attachment ids directly now — that is
// what turns a Stride-uploaded file embedded in a rich-text field from the
// dead "Jira attachment" chip into a real, named, downloadable file chip.

const NO_FIELDS = [];

function IssueScreenPanel({ issueKey, issueData, onFieldUpdate, onIssueCreated, onClose, onIssueDeleted, jiraFields: jiraFieldsProp = null, siteUrl = null, insightColumns = null, fieldNameMap = new Map(), insights = [], creationTree: creationTreeProp = null, onParentChange = null, focusCommentId = null, onOpenIssue = null }) {
  // The field catalogue: the host's, or — a host that passes none, like the full
  // work item page — the app's own (cached), so a field drawn by its TYPE (Line
  // items, Whiteboard, Document) is known by it even to a reader the screen
  // gives no edit metadata (no edit rights), instead of falling to a text box.
  const hostHasFields = Array.isArray(jiraFieldsProp) && jiraFieldsProp.length > 0;
  const { data: appFields } = useFields(siteUrl, { enabled: !hostHasFields });
  const jiraFields = hostHasFields ? jiraFieldsProp : (appFields?.length ? appFields : NO_FIELDS);
  // The hierarchy the panel works from: the host's tree when it passes one,
  // otherwise the one the panel resolves for the item's project (below).
  const [resolvedCreationTree, setResolvedCreationTree] = useState(null);
  const creationTree = creationTreeProp || resolvedCreationTree;
  const { user, userGroups } = useAuth();
  const { adminMode, openTab } = useTab();
  // Cached user lookup for the assignee/reporter/user-field pickers below.
  // Routes through the query cache so re-opening a picker (or opening three
  // of them on one screen) reuses one fetch per search term.
  const searchUsers = useUserSearch();
  useDateFormatVersion(); // re-render dates when admin changes global format
  // A key clicked in the panel (the breadcrumb, a row, the Parent field).
  // A host that can show another work item in place (the work item pop-up)
  // passes onOpenIssue, and the page behind stays where it is; otherwise —
  // and for this item's own key — it opens in Zebra Search as before.
  // Ctrl / middle click still opens a browser tab (the link's href).
  const handleIssueKeyClick = useCallback((key) => {
    if (!key) return;
    if (onOpenIssue && key !== issueKey) onOpenIssue(key);
    else openTab(`/issues?jql=${encodeURIComponent(`key = ${key}`)}`, key);
  }, [openTab, onOpenIssue, issueKey]);
  const [screenLayout, setScreenLayout] = useState(null);
  const [editMeta, setEditMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  // True when the /api/work-item/:key/screen batch endpoint is not
  // available (legacy/Innovation client) or returned no issue. Only
  // then does loadFields run its full parallel fetch. On the batch
  // path the panel paints exactly ONCE, from the batch payload alone —
  // the batch now carries ALL layout-deciding inputs (screenLayout,
  // editmeta, issueLayout), so there is no second fetch to flash a
  // re-render.
  const [batchFailed, setBatchFailed] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editDirtyRef = useRef(false);
  // Set only while the floating calendar is mounted — a click-outside guard
  // reads it to tell "a date editor is open" from "some other editor is".
  const dateCalendarRef = useRef(null);
  // Summary is edited from the panel TITLE (double-click), not from a field
  // row — it's in SKIP_FIELDS so it isn't printed twice on the screen.
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const summaryInputRef = useRef(null);
  // Multi-select: tracks an unsaved (debounce-pending) tick so click-away
  // flushes it exactly once — and skips a duplicate/empty save when nothing
  // changed or the debounce already saved.
  const msPendingRef = useRef(false);
  const [multiselectOptions, setMultiselectOptions] = useState(null);
  const [saving, setSaving] = useState({});
  const [saveSuccess, setSaveSuccess] = useState({});
  const [saveErrors, setSaveErrors] = useState({});
  const [editingParent, setEditingParent] = useState(false);
  // The Parent SCREEN FIELD's own editor (the Linked Work Items section has
  // editingParent). Same picker, same save; it just opens where the field is.
  const [editingParentField, setEditingParentField] = useState(false);
  const [parentSaving, setParentSaving] = useState(false);
  const [parentError, setParentError] = useState(null);
  const [parentSuccessMsg, setParentSuccessMsg] = useState(null);
  const [canDelete, setCanDelete] = useState(false);
  // Attachment controls follow the project's permission scheme: Upload
  // needs CREATE_ATTACHMENTS; the × on a card needs DELETE_ALL_ATTACHMENTS,
  // or DELETE_OWN_ATTACHMENTS when the person is the attachment's author.
  // The server refuses either way — this keeps the screen from offering
  // what the scheme withholds.
  const [attachmentPerms, setAttachmentPerms] = useState({ create: false, deleteOwn: false, deleteAll: false });
  // Security level (Jira's header lock). securityInfo is the project's
  // scheme + level menu from the batch ({ scheme, levels }; levels empty =
  // no scheme, no lock drawn); canSetSecurity is SET_ISSUE_SECURITY from the
  // same permissions call as Delete. The level itself lives on
  // fields.security ({ id, name }) like any other field.
  const [securityInfo, setSecurityInfo] = useState(null);
  const [canSetSecurity, setCanSetSecurity] = useState(false);
  const [securityMenuOpen, setSecurityMenuOpen] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const securityMenuRef = useRef(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const panelRef = useRef(null);
  const cancelRef = useRef(false);
  const [fullIssueData, setFullIssueData] = useState(null);
  // The parent this screen last saved (a work item, or null when removed);
  // undefined until it saves one. See parentInfo.
  const [savedParent, setSavedParent] = useState(undefined);
  const [mediaMap, setMediaMap] = useState(null);

  // Fetch full issue data on mount — ONE batch call that also pre-loads
  // the screen layout, editmeta, and permissions so the panel renders
  // once with the correct layout instead of flashing a default layout
  // first and re-rendering when the layout fetch resolves separately.
  //
  // Falls back to the legacy getIssue call if the batch endpoint fails
  // (safe on Innovation clients where the endpoint doesn't exist yet).
  useEffect(() => {
    if (!issueKey) return;
    let cancelled = false;
    setMediaMap(null); // Reset media map for new issue
    setLoading(true);
    setBatchFailed(false);
    setActiveTab(0);
    setError(null);
    api.getWorkItemScreen(issueKey).then(batch => {
      if (cancelled) return;
      const issue = batch?.issue;
      if (!issue) { setBatchFailed(true); return; }
      setFullIssueData(issue);
      // The batch carries every layout-deciding input, so apply the
      // whole payload and paint exactly once. No follow-up fetches —
      // a later state write here is what used to flash the layout.
      setScreenLayout(batch.screenLayout || null);
      setEditMeta(batch.editmeta || null);
      setIssueLayout(batch.issueLayout || null);
      // Comments ride in the batch (Jira-shaped { comments: [...] } from the
      // sub-handler). Setting them here makes loadComments a no-op on mount
      // (comments !== null), killing the duplicate GET /comments. [] is a
      // real value, so an issue with zero comments also counts as "loaded".
      setComments(batch.comments?.comments || []);
      if (batch.permissions) {
        const perms = batch.permissions.permissions || {};
        if (perms.DELETE_ISSUES?.havePermission) setCanDelete(true);
        if (perms.SET_ISSUE_SECURITY?.havePermission) setCanSetSecurity(true);
        setAttachmentPerms(readAttachmentPerms(perms));
      }
      setSecurityInfo(batch.security || null);
      setLoading(false);
      if (issue.fields?.attachment?.length > 0) {
        applyFieldUpdate(issueKey, { attachment: issue.fields.attachment });
      }
    }).catch(async (err) => {
      // Batch endpoint unavailable (e.g. Innovation client without the
      // Stride router). Fall back to the legacy single-issue fetch so
      // the panel still opens; batchFailed=true makes loadFields do the
      // layout fetches as before.
      console.warn('[IssueScreenPanel] batch endpoint failed, falling back to legacy:', err?.message || err);
      if (cancelled) return;
      setBatchFailed(true);
      try {
        const issue = await api.getIssue(issueKey, '*all', siteUrl);
        if (cancelled || !issue) return;
        setFullIssueData(issue);
        if (issue.fields?.attachment?.length > 0) {
          applyFieldUpdate(issueKey, { attachment: issue.fields.attachment });
        }
      } catch { /* silent */ }
    });
    return () => { cancelled = true; };
  }, [issueKey, siteUrl]);

  // Record this work item as "recently viewed" for the current account.
  // This panel is the shared detail view for both Folders and Zebra Search,
  // so hooking it here captures every open. Fire-and-forget — a failure must
  // never disrupt the panel, and it backs the search-bar "Recently Viewed"
  // dropdown (per-account, server-side; see modules/recently-viewed/).
  useEffect(() => {
    if (!issueKey) return;
    api.recordRecentlyViewed(issueKey).catch(() => {});
  }, [issueKey]);

  // Merge webhook updates directly into fullIssueData (no re-fetch needed).
  //
  // We INTENTIONALLY do NOT call isDuplicateIssuesChangedEvent here.
  // That dedup function lives in module-level state shared across
  // every component that subscribes to issues-changed, so whichever
  // handler runs first (Structure.jsx beats us by mounting earlier)
  // claims the event and the panel's handler always saw it as a
  // duplicate — meaning the panel never merged fields from webhooks.
  // The merge below is idempotent (spread overrides), so running it
  // for every event delivery is safe even if socket.io ever delivers
  // the same event twice during a reconnect.
  useEffect(() => {
    if (!issueKey) return;
    const handleIssuesChanged = (payload) => {
      const { event, issueKey: changedKey, issueData: updatedData, timestamp } = payload || {};
      if (event !== 'jira:issue_updated') return;
      if (changedKey !== issueKey) return;
      if (!updatedData?.fields) {
        console.log(`[IssueScreen][WS] ${event} for ${issueKey} arrived but payload had no fields — skip merge (ts=${timestamp})`);
        return;
      }
      const incomingKeys = Object.keys(updatedData.fields);
      // Diagnostic: any field whose key looks like a date — full
      // values, not truncated. Helps spot 'startDate' coming back
      // null because Jira stores under customfield_NNNNN, or vice
      // versa.
      const dateKeyHits = incomingKeys.filter(k => /date|due|sched|start/i.test(k));
      const dateValuePairs = dateKeyHits.map(k => `${k}=${JSON.stringify(updatedData.fields[k])}`);
      console.log(`[IssueScreen][WS] merge for ${issueKey} (ts=${timestamp}, ${incomingKeys.length} fields)`);
      if (dateValuePairs.length > 0) {
        console.log(`[IssueScreen][WS]   date-ish fields in payload: ${dateValuePairs.join(' | ')}`);
      }
      // A parent this screen saved follows a later change made elsewhere
      // (a script, another person) instead of hiding it. Only a parent WITH
      // a key replaces it: a type the hierarchy hangs by a link carries no
      // fields.parent at all, and that must not read as "no parent".
      if (updatedData.fields.parent?.key) {
        setSavedParent(prev => (prev === undefined ? prev : updatedData.fields.parent));
      }
      setFullIssueData(prev => {
        const next = prev ? { ...prev, fields: { ...prev.fields, ...updatedData.fields } } : updatedData;
        // Diagnostic: what mergedIssueData would render after this
        // merge for the same date-ish keys.
        if (dateKeyHits.length > 0) {
          const after = dateKeyHits.map(k => `${k}=${JSON.stringify(next.fields?.[k])}`);
          console.log(`[IssueScreen][WS]   after merge fullIssueData has: ${after.join(' | ')}`);
        }
        return next;
      });
    };
    socket.on('issues-changed', handleIssuesChanged);
    return () => { socket.off('issues-changed', handleIssuesChanged); };
  }, [issueKey]);

  // Local wrapper around the parent's onFieldUpdate. Keeps fullIssueData
  // in sync with every local edit so mergedIssueData's overlay doesn't
  // render a just-saved field with the stale mount-time /api/issue fetch.
  // Without this, edits flashed green but UI showed the old value until
  // the user refreshed the page. Skips the fullIssueData patch when the
  // update targets a different issue (e.g., child edits from this panel).
  const applyFieldUpdate = useCallback((key, fields) => {
    if (onFieldUpdate) onFieldUpdate(key, fields);
    if (key === issueKey) {
      setFullIssueData(prev => prev ? { ...prev, fields: { ...prev.fields, ...fields } } : prev);
    }
  }, [issueKey, onFieldUpdate]);

  // Merge: full issue fields overlay the partial structure data
  const mergedIssueData = useMemo(() => {
    if (!fullIssueData) return issueData;
    return {
      ...issueData,
      ...fullIssueData,
      fields: { ...issueData?.fields, ...fullIssueData.fields }
    };
  }, [issueData, fullIssueData]);

  // Keep panel pinned to the visible area when scrolling horizontally
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const scrollContainer = panel.closest('.structure-table-wrapper');
    if (!scrollContainer) return;
    const update = () => {
      panel.style.width = scrollContainer.clientWidth + 'px';
      panel.style.transform = `translateX(${scrollContainer.scrollLeft}px)`;
    };
    update();
    scrollContainer.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scrollContainer);
    return () => {
      scrollContainer.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  const [issueLayout, setIssueLayout] = useState(null);
  // Discard-confirmation dialog (replaces window.confirm for a polished look).
  // null = closed. When open, holds the callback to run on Discard.
  const [discardConfirm, setDiscardConfirm] = useState(null);

  // Beforeunload guard: warn the user before closing the tab / navigating away
  // when there are unsaved edits. Standard browser-native dialog — we can't
  // customise the message in modern browsers; the prompt just confirms the
  // user wants to leave. Registered whenever a field editor is OPEN and the
  // dirty check happens inside the handler — editDirtyRef is a ref (typing
  // doesn't re-render), so gating registration on it would leave the guard
  // permanently unarmed: the editor opens clean and dirties later without
  // this effect ever re-running.
  useEffect(() => {
    if (!editingFieldId) return undefined;
    const handler = (e) => {
      if (!editDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editingFieldId]);

  // Click away from the floating calendar to close it. Deliberately NOT for
  // every editor: text and number save what you typed on blur, so closing
  // them from out here would race that save. The calendar has nothing
  // unsaved — a day click IS the save — so leaving it is always free.
  // dateCalendarRef is null unless the calendar is the open editor, which is
  // what keeps this from firing for the others.
  useEffect(() => {
    if (!editingFieldId) return undefined;
    const handler = (e) => {
      const cal = dateCalendarRef.current;
      if (!cal || cal.contains(e.target)) return;
      editDirtyRef.current = false;
      setEditingFieldId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editingFieldId]);

  // Status transition state
  const [transitions, setTransitions] = useState([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionError, setTransitionError] = useState(null);
  const [transitionReasons, setTransitionReasons] = useState([]);   // validator/condition reasons when a transition is blocked
  const [transitioningName, setTransitioningName] = useState(null);
  const statusDropdownRef = useRef(null);

  // Panel-level insight picker state
  const [panelInsightId, setPanelInsightId] = useState(null);
  const [showPanelInsightPicker, setShowPanelInsightPicker] = useState(false);
  const panelInsightRef = useRef(null);

  // Activity state
  const [activityTab, setActivityTab] = useState('comments');
  const [historySortOrder, setHistorySortOrder] = useState('desc'); // 'asc' | 'desc'
  const [comments, setComments] = useState(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  const [changelog, setChangelog] = useState(null);
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [changelogError, setChangelogError] = useState(null);
  const [newCommentText, setNewCommentText] = useState(null);
  const [addingComment, setAddingComment] = useState(false);
  const [addCommentError, setAddCommentError] = useState(null);
  // SERVICE DESK ONLY. A desk ticket has two audiences — the customer
  // reading the portal and the agents talking about them — so the composer
  // makes the agent choose. Every other project has one audience and never
  // sees this: an ordinary comment box must not grow a question that has
  // only one answer.
  const [isServiceDesk, setIsServiceDesk] = useState(false);
  const [portalRequest, setPortalRequest] = useState(null);
  const [commentInternal, setCommentInternal] = useState(false);
  const [showCommentEditor, setShowCommentEditor] = useState(false);
  // Threading / moderation state. `replyingTo` is the id of the thread ROOT
  // whose reply box is open (one at a time, like Jira); `commentSortOrder`
  // mirrors the History tab's toggle; `deletingComment` holds the comment
  // pending confirmation.
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState(null);
  const [commentSortOrder, setCommentSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [deletingComment, setDeletingComment] = useState(null);
  const [deletingCommentBusy, setDeletingCommentBusy] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [fetchedAttachments, setFetchedAttachments] = useState(null);
  // Index into `attachments` of the file open in the preview overlay; null
  // when closed. A plain click on a card opens it here; middle/Ctrl-click
  // keeps the card's href and goes to a new browser tab.
  const [previewIndex, setPreviewIndex] = useState(null);
  const attachmentInputRef = useRef(null);
  const [panelDragActive, setPanelDragActive] = useState(false);
  const panelDragCounterRef = useRef(0);

  // Sidebar resize state
  const [sidebarWidthPct, setSidebarWidthPct] = useState(33);
  const isDraggingDivider = useRef(false);
  const columnsRef = useRef(null);

  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingDivider.current = true;
    document.body.classList.add('resizing-panel');

    const onMouseMove = (ev) => {
      if (!isDraggingDivider.current || !columnsRef.current) return;
      const rect = columnsRef.current.getBoundingClientRect();
      const pct = ((rect.right - ev.clientX) / rect.width) * 100;
      const clamped = Math.min(55, Math.max(15, pct));
      setSidebarWidthPct(clamped);
    };

    const onMouseUp = () => {
      isDraggingDivider.current = false;
      document.body.classList.remove('resizing-panel');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // Link issue state
  const [showLinkIssue, setShowLinkIssue] = useState(false);
  const [linkForm, setLinkForm] = useState({ linkTypeId: '', direction: 'outward', targetIssueKey: '' });
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [linkTypes, setLinkTypes] = useState(null);

  // Hierarchy-aware "+ Add link" flow — opened by the + button at the top
  // of the Relationships section. Two-step: pickRelationship → pickIssue,
  // then creates the link/subtask. Step 1 auto-skips
  // when the hierarchy allows only one possible relationship.
  //
  // Shape: null | {
  //   step: 'pickRelationship' | 'pickIssue',
  //   option: <option> | null,    // chosen option: { role, candidateTypeName, edge }
  //   query: string,
  // }
  //
  // Each option has role ∈ {'child', 'parent'}:
  //   - role: 'child'  → current item is the parent, candidate becomes the child
  //   - role: 'parent' → current item is the child, candidate becomes the parent
  const [addingRelationship, setAddingRelationship] = useState(null);
  const [addingChildSubmitting, setAddingChildSubmitting] = useState(false);
  const [addingChildError, setAddingChildError] = useState(null);
  // Flat array of every work item key reachable from this item through
  // any hierarchy edge — direct children + grandchildren + deeper. Used
  // to hide already-in-hierarchy candidates from the work-item picker.
  // Fetched once per side-panel open via a BFS endpoint; null while
  // loading, so the picker only excludes direct links until it arrives.
  const [hierarchyDescendantKeys, setHierarchyDescendantKeys] = useState(null);

  // Header "+ Create" menu (the Architectural Hierarchy's children of this
  // item's type) — wired below next to the create modal state.
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef(null);


  // The loaded item, not the prop: a pop-up opened from a key alone
  // (customs' useWorkItemPopup passes { key, fields: {} }) has no project or
  // type on issueData, and View Workflow did nothing at all.
  const handleViewWorkflow = useCallback(() => {
    const fields = mergedIssueData?.fields;
    const projectId = fields?.project?.id;
    const issueTypeId = fields?.issuetype?.id;
    const issueTypeName = fields?.issuetype?.name || 'Workflow';
    if (!projectId || !issueTypeId) return;
    openTab(`/workflow?projectId=${projectId}&issueTypeId=${issueTypeId}`, `${issueTypeName} Workflow`);
  }, [mergedIssueData, openTab]);

  // Create issue modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalMode, setCreateModalMode] = useState('subtask');
  const [createModalLinkInfo, setCreateModalLinkInfo] = useState(null);
  // Header "+ Create" — the Architectural Hierarchy's children of this
  // item's type. The menu lists them; picking one opens the create modal in
  // hierarchy mode with that type preselected (hierarchyCreate is state so
  // the modal's load effect sees one stable object, not a fresh one per
  // render).
  const [hierarchyCreateOptions, setHierarchyCreateOptions] = useState(null); // null = loading
  const [hierarchyCreate, setHierarchyCreate] = useState(null);

  const panelProjectKey = mergedIssueData?.fields?.project?.key || issueData?.fields?.project?.key || null;
  const panelTypeName = mergedIssueData?.fields?.issuetype?.name || issueData?.fields?.issuetype?.name || null;

  // Resolve the project's hierarchy when the host passed none.
  useEffect(() => {
    if (creationTreeProp || !panelProjectKey) { setResolvedCreationTree(null); return; }
    if (hierarchyTreeByProject.has(panelProjectKey)) {
      setResolvedCreationTree(hierarchyTreeByProject.get(panelProjectKey));
      return;
    }
    let cancelled = false;
    (async () => {
      let tree = null;
      try {
        const schemes = await api.getProjectIssueTypeSchemes([panelProjectKey]);
        const schemeId = schemes?.[panelProjectKey]?.schemeId;
        if (schemeId) {
          // 404 = the scheme has no tree; that is a plain "none", not an error.
          const found = await api.getCreationTreeByScheme(schemeId).catch(() => null);
          tree = found?.treeData || null;
        }
      } catch {
        tree = null;
      }
      hierarchyTreeByProject.set(panelProjectKey, tree);
      if (!cancelled) setResolvedCreationTree(tree);
    })();
    return () => { cancelled = true; };
  }, [creationTreeProp, panelProjectKey]);

  // The tree's children of this type — decides whether the button is live.
  const hierarchyChildEdges = useMemo(
    () => (creationTree && panelTypeName ? getAllowedChildTypes(panelTypeName, creationTree, LINK_TYPE_NAME) : []),
    [creationTree, panelTypeName],
  );

  // Opening the menu narrows the tree's children to the project's live types.
  useEffect(() => {
    if (!createMenuOpen) return;
    if (!panelProjectKey || hierarchyChildEdges.length === 0) { setHierarchyCreateOptions([]); return; }
    let cancelled = false;
    setHierarchyCreateOptions(null);
    const cached = issueTypesByProject.get(panelProjectKey);
    const load = cached ? Promise.resolve(cached) : api.getProjectIssueTypes(panelProjectKey).then((types) => {
      issueTypesByProject.set(panelProjectKey, types || []);
      return types || [];
    });
    load
      .then((types) => { if (!cancelled) setHierarchyCreateOptions(resolveHierarchyCreateOptions(panelTypeName, creationTree, types, LINK_TYPE_NAME)); })
      .catch(() => { if (!cancelled) setHierarchyCreateOptions([]); });
    return () => { cancelled = true; };
  }, [createMenuOpen, panelProjectKey, panelTypeName, creationTree, hierarchyChildEdges]);

  const handleHierarchyCreatePick = useCallback((option) => {
    setCreateMenuOpen(false);
    setHierarchyCreate({ options: hierarchyCreateOptions || [], initialTypeId: option.issueTypeId });
    setCreateModalLinkInfo(null);
    setCreateModalMode('hierarchy');
    setCreateModalOpen(true);
  }, [hierarchyCreateOptions]);

  // Epic/parent children state (non-subtask children fetched via JQL)
  const [fetchedChildren, setFetchedChildren] = useState([]);
  // Bulk edit from a related section's ••• menu: the keys it edits, and a
  // bump that re-reads the rows once it has run. Declared HERE, before the
  // children / row-details effects that list relatedRefresh in their deps —
  // declared below them it threw "Cannot access before initialization" on
  // every render of the panel (2026-10-08).
  const [relatedBulkKeys, setRelatedBulkKeys] = useState(null);
  const { edit: canBulkEdit } = useBulkAccess(); // the server's Bulk Edit Access rule
  const [relatedRefresh, setRelatedRefresh] = useState(0);

  // Enriched data for insight columns on child/linked items
  const [enrichedDataMap, setEnrichedDataMap] = useState({});
  const [enrichedLoading, setEnrichedLoading] = useState(false);
  const [childEditingField, setChildEditingField] = useState(null);
  const [childInlineEdit, setChildInlineEdit] = useState(null); // { issueKey, fieldId, value, original? }
  // The columns this person added to each related-items table with its "+"
  // ({ subtasks, children, links } → field ids), for this item's work type,
  // and the open picker ({ section, anchor }) — see relatedColumns.js.
  const relatedWorkTypeId = mergedIssueData?.fields?.issuetype?.id || null;
  const [relatedColIds, setRelatedColIds] = useState({});
  const [relatedColPicker, setRelatedColPicker] = useState(null);
  useEffect(() => {
    setRelatedColIds(Object.fromEntries(RELATED_SECTIONS.map(s => [s, readRelatedColumnIds(s, relatedWorkTypeId)])));
    setRelatedColPicker(null);
  }, [relatedWorkTypeId]);
  const toggleRelatedColumn = useCallback((section, fieldId) => {
    setRelatedColIds(prev => {
      const next = toggleRelatedColumnId(prev[section] || [], fieldId);
      writeRelatedColumnIds(section, relatedWorkTypeId, next);
      return { ...prev, [section]: next };
    });
  }, [relatedWorkTypeId]);
  const resetRelatedColumns = useCallback((section) => {
    writeRelatedColumnIds(section, relatedWorkTypeId, []);
    setRelatedColIds(prev => ({ ...prev, [section]: [] }));
  }, [relatedWorkTypeId]);

  // Fetch the full descendant chain of this work item via the hierarchy's
  // own definition of "child" edges. Used to hide grandchildren and
  // deeper descendants from the work-item picker — they're already part
  // of this item's tree and shouldn't be offered for re-linking. Refreshes
  // when the panel opens a new item OR when the hierarchy changes.
  useEffect(() => {
    if (!issueKey || !creationTree) {
      setHierarchyDescendantKeys(null);
      return;
    }
    const edges = extractAllEdgesFromTree(creationTree);
    if (!edges.length) {
      setHierarchyDescendantKeys([]);
      return;
    }
    let cancelled = false;
    setHierarchyDescendantKeys(null);
    api.getDescendantKeys(issueKey, edges)
      .then(data => {
        if (cancelled) return;
        setHierarchyDescendantKeys(Array.isArray(data?.keys) ? data.keys : []);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[IssueScreenPanel] descendant-keys fetch failed (picker will only exclude direct links):', err?.message || err);
        setHierarchyDescendantKeys([]);
      });
    return () => { cancelled = true; };
  }, [issueKey, creationTree]);

  // Close create menu on outside click
  useEffect(() => {
    if (!createMenuOpen) return;
    const handler = (e) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target)) {
        setCreateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [createMenuOpen]);

  // Insight columns for child/linked mini-tables (filter out 'key' since it's shown as fixed left element)
  const panelColumns = useMemo(() => {
    if (!insightColumns || insightColumns.length === 0) return null;
    return insightColumns.filter(c => c.fieldId !== 'key');
  }, [insightColumns]);

  // If a panel-level insight is selected, use its columns instead of the folder default
  const effectivePanelColumns = useMemo(() => {
    if (panelInsightId === null) return panelColumns; // folder default
    const ins = insights.find(i => i.id === panelInsightId);
    if (!ins?.columns?.length) return panelColumns;
    return ins.columns
      .map((col, i) => ({
        id: `insight-${ins.id}-${i}`,
        name: col.name,
        type: col.type || 'field',
        fieldId: col.fieldId,
        formula: col.formula,
        formulaLanguage: ins.formulaLanguage || 'js',
        width: col.width || 150,
        aggregateMode: col.aggregateMode || 'none',
        aggregateFormula: col.aggregateFormula || null,
      }))
      .filter(c => c.fieldId !== 'key');
  }, [panelInsightId, panelColumns, insights]);

  const isEpicType = (mergedIssueData?.fields?.issuetype?.name || '').toLowerCase() === 'epic';

  // Minimal fields needed to display child/linked issue rows
  // assignee: every related row shows who has it, as Jira's tables do.
  const CHILD_DISPLAY_FIELDS = 'summary,status,issuetype,priority,assignee';

  // The children are two searches, each kept per work item (`key`) so a
  // result that lands after the panel moved on to another item is ignored:
  //   parent = KEY        — every child by its parent field. Starts the
  //                         moment the key is known, NOT after the work item
  //                         has loaded: it needs nothing from it, and waiting
  //                         made "Child work items" pop in seconds after the
  //                         rest of the screen whenever the server was busy.
  //                         A sub-task's search simply comes back empty.
  //   "Epic Link" = KEY   — an epic's classic children; needs the type, so it
  //                         runs once the work item is known to be an epic.
  // Neither reads a total, so both skip the COUNT. fetchedChildren stays the
  // one list the rest of the panel reads (and edits in place).
  const [childSources, setChildSources] = useState({ key: null, parent: [], epic: [] });
  const searchChildren = useCallback((jql) => api.searchIssues(jql, CHILD_DISPLAY_FIELDS, 200, siteUrl, 0, true)
    .then(r => r.issues || []).catch(() => []), [siteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!issueKey) return undefined;
    setChildSources(prev => (prev.key === issueKey ? prev : { key: issueKey, parent: [], epic: [] }));
    let cancelled = false;
    searchChildren(`parent = ${issueKey}`).then(issues => {
      if (!cancelled) setChildSources(prev => (prev.key === issueKey ? { ...prev, parent: issues } : prev));
    });
    return () => { cancelled = true; };
  }, [issueKey, searchChildren, relatedRefresh]);

  useEffect(() => {
    if (!issueKey || !isEpicType) return undefined;
    let cancelled = false;
    searchChildren(`"Epic Link" = ${issueKey}`).then(issues => {
      if (!cancelled) setChildSources(prev => (prev.key === issueKey ? { ...prev, epic: issues } : prev));
    });
    return () => { cancelled = true; };
  }, [issueKey, isEpicType, searchChildren, relatedRefresh]);

  // Merge and de-duplicate into the list the panel reads.
  useEffect(() => {
    if (childSources.key !== issueKey) { setFetchedChildren([]); return; }
    const seen = new Set();
    const children = [];
    for (const issue of [...childSources.parent, ...childSources.epic]) {
      if (issue?.key && !seen.has(issue.key)) { seen.add(issue.key); children.push(issue); }
    }
    setFetchedChildren(children);
  }, [childSources, issueKey]);

  // Fetch enriched data for child/linked issues — only the fields needed by insight columns
  // Runs with or without extra columns: the rows' own status / assignee /
  // icon come from here too.
  useEffect(() => {
    const subtasks = mergedIssueData?.fields?.subtasks || [];
    const issueLinks = mergedIssueData?.fields?.issuelinks || [];
    // Collect all child/linked issue keys
    const keys = new Set();
    for (const sub of subtasks) { if (sub.key) keys.add(sub.key); }
    for (const link of issueLinks) {
      if (link.inwardIssue?.key) keys.add(link.inwardIssue.key);
      if (link.outwardIssue?.key) keys.add(link.outwardIssue.key);
    }
    for (const child of fetchedChildren) { if (child.key) keys.add(child.key); }

    if (keys.size === 0) {
      setEnrichedDataMap({});
      return;
    }

    // Build the field list: display fields + insight column fields + the
    // columns added with a table's "+" (no *all)
    const columnFields = [
      ...(effectivePanelColumns || [])
        .filter(c => c.type !== 'formula' && c.fieldId)
        .map(c => c.fieldId),
      ...Object.values(relatedColIds).flat(),
    ];
    const fieldSet = new Set([...CHILD_DISPLAY_FIELDS.split(','), ...columnFields]);
    const fields = [...fieldSet].join(',');

    const toFetch = [...keys];
    let cancelled = false;
    setEnrichedLoading(true);

    const fetchEnriched = async () => {
      const allIssues = {};
      // Batch in chunks of 50 to avoid JQL size limits
      for (let i = 0; i < toFetch.length; i += 50) {
        const chunk = toFetch.slice(i, i + 50);
        const jql = `key in (${chunk.join(',')})`;
        try {
          const result = await api.searchIssues(jql, fields, chunk.length, siteUrl);
          if (cancelled) return;
          for (const issue of (result.issues || [])) {
            allIssues[issue.key] = issue;
          }
        } catch {
          // Continue with partial data
        }
      }
      if (!cancelled) {
        setEnrichedDataMap(allIssues);
        setEnrichedLoading(false);
      }
    };
    fetchEnriched();
    return () => { cancelled = true; };
  }, [effectivePanelColumns, relatedColIds, mergedIssueData?.fields?.subtasks, mergedIssueData?.fields?.issuelinks, fetchedChildren, siteUrl, relatedRefresh]);

  // Delete issue handler
  const handleDeleteIssue = async () => {
    setDeleting(true);
    try {
      await api.deleteIssue(issueKey, siteUrl);
      setDeleteConfirm(false);
      if (onIssueDeleted) {
        onIssueDeleted(issueKey);
      } else {
        onClose?.();
      }
    } catch (err) {
      setSaveErrors(prev => ({ ...prev, __delete: extractErrorMessage(err, 'Failed to delete work item') }));
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  // Fetch issue layout + screen layout + editmeta in parallel.
  //
  // Runs ONLY when the batch endpoint is unavailable (batchFailed) or
  // on an explicit manual refresh — on the batch path the panel gets
  // every layout input from /api/work-item/:key/screen in one payload
  // and never calls this. A generation counter guards against a stale
  // in-flight fetch applying state over a newer call's results (the
  // old `cancelRef.current = true; = false` two-liner was synchronous
  // and cancelled nothing — that stale write was the layout flash).
  const loadGenRef = useRef(0);
  const loadFields = useCallback((refresh = false) => {
    const gen = ++loadGenRef.current;
    setError(null);
    setActiveTab(0);

    const projectId = mergedIssueData?.fields?.project?.id;
    const issueTypeId = mergedIssueData?.fields?.issuetype?.id;

    const issueLayoutPromise = (projectId && issueTypeId)
      ? api.getIssueLayout(projectId, issueTypeId, { refresh, context: 'view' }).catch(() => null)
      : Promise.resolve(null);

    setLoading(true);
    const screenPromise = (projectId && issueTypeId)
      ? api.getScreenLayout(projectId, issueTypeId, 'view', refresh).catch(() => null)
      : Promise.resolve(null);
    const editMetaPromise = api.getEditMeta(issueKey, siteUrl).catch(() => null);

    Promise.all([issueLayoutPromise, screenPromise, editMetaPromise])
      .then(([layout, screen, meta]) => {
        if (gen !== loadGenRef.current || cancelRef.current) return;
        setIssueLayout(layout);
        setScreenLayout(screen);
        setEditMeta(meta);
        setLoading(false);

        if (!layout && !screen && !meta) {
          setError('Could not load field information');
        }
      })
      .catch(err => {
        if (gen === loadGenRef.current && !cancelRef.current) {
          setError(extractErrorMessage(err, 'Failed to load fields'));
          setLoading(false);
        }
      });
  }, [issueKey, mergedIssueData?.fields?.project?.id, mergedIssueData?.fields?.issuetype?.id]);

  useEffect(() => {
    if (!batchFailed) return;
    loadFields(false);
  }, [batchFailed, loadFields]);

  // Cleared on every mount, not only set on unmount: React mounts a screen,
  // unmounts it and mounts it again in development (StrictMode), and a flag
  // left true from that first unmount made every later loadFields drop its
  // answer — "Loading fields…" for ever.
  useEffect(() => {
    cancelRef.current = false;
    return () => { cancelRef.current = true; };
  }, []);

  // Delete permission: the batch supplies permissions.DELETE_ISSUES on the
  // happy path (→ setCanDelete in the batch .then), so this standalone
  // check is ONLY the fallback for batchFailed clients (Innovation), which
  // have no batch permissions — without it they'd never see the Delete
  // button. The !batchFailed gate skips the redundant round-trip on the
  // happy path (same pattern as the attachment + comments effects).
  // canDelete/deleteConfirm are reset on issueKey change in the
  // reset-activity-state effect below (which runs on BOTH paths).
  useEffect(() => {
    if (!batchFailed) return;
    if (!issueKey) return;
    api.checkPermissions(issueKey, PANEL_PERMISSION_KEYS, siteUrl)
      .then(data => {
        const perms = data?.permissions || {};
        if (perms.DELETE_ISSUES?.havePermission) setCanDelete(true);
        if (perms.SET_ISSUE_SECURITY?.havePermission) setCanSetSecurity(true);
        setAttachmentPerms(readAttachmentPerms(perms));
      })
      .catch(() => {});
    // The level menu rides the batch on the happy path; here it is its own
    // call (an issue key resolves to its project server-side).
    api.getProjectSecurityLevels(issueKey).then(setSecurityInfo).catch(() => {});
  }, [issueKey, siteUrl, batchFailed]);

  // Reset activity state when issueKey changes
  useEffect(() => {
    setCanDelete(false);
    setCanSetSecurity(false);
    setSecurityInfo(null);
    setSecurityMenuOpen(false);
    setAttachmentPerms({ create: false, deleteOwn: false, deleteAll: false });
    setDeleteConfirm(false);
    setComments(null);
    setCommentsError(null);
    setChangelog(null);
    setChangelogError(null);
    setShowCommentEditor(false);
    setNewCommentText(null);
    setAddCommentError(null);
    setReplyingTo(null);
    setReplyText(null);
    setDeletingComment(null);
    setActivityTab('comments');
    setCreateModalOpen(false);
    setCreateMenuOpen(false);
    setHierarchyCreate(null);
    setShowLinkIssue(false);
    setLinkForm({ linkTypeId: '', direction: 'outward', targetIssueKey: '' });
    setLinkError(null);
    setLinkSuccess(false);
    setFetchedAttachments(null);
    setPreviewIndex(null);
    setEditingParentField(false);
  }, [issueKey]);

  // Attachment refetch — ONLY on the batchFailed fallback. On the happy
  // path the batch's issue already carries attachments (applied via
  // applyFieldUpdate in the batch .then), so this is redundant there. The
  // OLD guard (mergedIssueData.fields.attachment.length>0) was false on
  // first render — folder-row snapshots omit attachments — so it never
  // actually skipped, and because the stride /api/issue/:key handler
  // IGNORES the ?fields=attachment param and re-runs the FULL issue
  // pipeline, this fired a wasted full fetch on every panel open.
  useEffect(() => {
    if (!issueKey) return;
    if (!batchFailed) return;
    let cancelled = false;
    api.getIssue(issueKey, 'attachment', siteUrl).then(issue => {
      if (!cancelled && issue?.fields?.attachment?.length > 0) {
        setFetchedAttachments(issue.fields.attachment);
        // Also update parent so data stays in sync
        applyFieldUpdate(issueKey, { attachment: issue.fields.attachment });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [issueKey, siteUrl, batchFailed]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (createModalOpen) {
          // Modal handles its own Escape
          return;
        } else if (childInlineEdit) {
          setChildInlineEdit(null);
        } else if (childEditingField) {
          setChildEditingField(null);
        } else if (showLinkIssue) {
          setShowLinkIssue(false);
          setLinkError(null);
        } else if (replyingTo) {
          setReplyingTo(null);
          setReplyText(null);
        } else if (showCommentEditor) {
          setShowCommentEditor(false);
          setNewCommentText(null);
        } else if (editingFieldId) {
          editDirtyRef.current = false;
          setEditingFieldId(null);
        } else {
          // A panel shown as a page of its own (no onClose) has nothing to
          // close — Escape must not reach into the tab around it.
          onClose?.();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editingFieldId, showCommentEditor, replyingTo, createModalOpen, showLinkIssue, childEditingField, childInlineEdit, onClose]);

  // Fetch transitions every time the status dropdown opens — the
  // available set depends on the issue's current field values (e.g. a
  // JWT fieldsRequiredCondition that gates visibility), so the list
  // goes stale the moment any field changes. The folder cell already
  // refetches on each open; this matches that behaviour. The previous
  // `transitions.length === 0` guard cached the first response and
  // only refreshed after an apply, missing field-edit invalidations.
  useEffect(() => {
    if (!statusDropdownOpen) return;
    api.getTransitions(issueKey, siteUrl)
      .then(data => setTransitions(data.transitions || []))
      .catch(() => setTransitions([]));
  }, [statusDropdownOpen, issueKey, siteUrl]);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClick = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdownOpen]);

  // Reset panel insight when the panel opens a different issue
  useEffect(() => {
    setPanelInsightId(null);
    setShowPanelInsightPicker(false);
  }, [issueKey]);

  // Close panel insight picker on outside click
  useEffect(() => {
    if (!showPanelInsightPicker) return;
    const handleClick = (e) => {
      if (panelInsightRef.current && !panelInsightRef.current.contains(e.target)) {
        setShowPanelInsightPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPanelInsightPicker]);

  // Lazy-load link issue data
  const loadLinkIssueData = useCallback(() => {
    if (!linkTypes) {
      api.getIssueLinkTypes().then(data => {
        const types = data.issueLinkTypes || [];
        setLinkTypes(types);
        if (types.length > 0) setLinkForm(prev => ({ ...prev, linkTypeId: prev.linkTypeId || types[0].id }));
      }).catch(() => setLinkTypes([]));
    }
  }, [linkTypes]);

  // Ensure linkTypes is loaded the moment the add-relationship panel opens
  // so the per-option badges render the actual Jira inward/outward phrase
  // (e.g. "Link as shipment item") rather than the literal fallback word
  // ("inward" / "outward"). Without this, opening the picker before any
  // other action that triggers loadLinkIssueData leaves the badges
  // unresolved.
  useEffect(() => {
    if (addingRelationship) loadLinkIssueData();
  }, [addingRelationship, loadLinkIssueData]);

  // …and the project's work types, so each relationship lists its types
  // with their icons.
  const [relTypeIconByName, setRelTypeIconByName] = useState(null);
  const addingRelationshipOpen = !!addingRelationship;
  useEffect(() => {
    if (!addingRelationshipOpen || relTypeIconByName || !panelProjectKey) return;
    let cancelled = false;
    const cached = issueTypesByProject.get(panelProjectKey);
    const load = cached ? Promise.resolve(cached) : api.getProjectIssueTypes(panelProjectKey).then((types) => {
      issueTypesByProject.set(panelProjectKey, types || []);
      return types || [];
    });
    load
      .then((types) => {
        if (cancelled) return;
        setRelTypeIconByName(new Map((types || []).filter(t => t?.iconUrl).map(t => [String(t.name || '').toLowerCase(), t.iconUrl])));
      })
      .catch(() => { if (!cancelled) setRelTypeIconByName(new Map()); });
    return () => { cancelled = true; };
  }, [addingRelationshipOpen, relTypeIconByName, panelProjectKey]);

  // Handle modal created callback
  const handleModalCreated = useCallback((result, createdMode, returnedLinkInfo) => {
    if (!onFieldUpdate) return;
    if (createdMode === 'subtask') {
      const existingSubtasks = mergedIssueData?.fields?.subtasks || [];
      const newSubtask = {
        id: result.id,
        key: result.key,
        fields: {
          summary: result.key,
          status: { name: 'To Do', statusCategory: { colorName: 'blue-gray' } },
          issuetype: { name: 'Sub-task' },
          priority: null,
        },
      };
      applyFieldUpdate(issueKey, { subtasks: [...existingSubtasks, newSubtask] });
    } else if (createdMode === 'hierarchy' && returnedLinkInfo) {
      // returnedLinkInfo is the hierarchy EDGE the child was created on.
      const edge = returnedLinkInfo;
      const childStub = {
        id: result.id,
        key: result.key,
        fields: {
          summary: result.key,
          status: { name: 'To Do', statusCategory: { colorName: 'blue-gray' } },
          issuetype: { name: edge.issueTypeName || '', iconUrl: edge.iconUrl || undefined },
          priority: null,
        },
      };
      if (edge.relationshipType === 'subtask' || edge.relationshipType === 'child') {
        const existingSubtasks = mergedIssueData?.fields?.subtasks || [];
        applyFieldUpdate(issueKey, { subtasks: [...existingSubtasks, childStub] });
      } else {
        // Same perspective rule as handleAddRelationshipSubmit: direction
        // 'outward' put the CHILD in inwardIssue, so from the parent's
        // issuelinks the child sits on the inward side (and vice versa).
        const existingLinks = mergedIssueData?.fields?.issuelinks || [];
        const type = { name: edge.linkTypeName || LINK_TYPE_NAME };
        const newLink = edge.linkDirection === 'inward'
          ? { id: 'tmp-' + Date.now(), type, outwardIssue: childStub }
          : { id: 'tmp-' + Date.now(), type, inwardIssue: childStub };
        applyFieldUpdate(issueKey, { issuelinks: [...existingLinks, newLink] });
      }
      // The stub carries the key as its summary; the real fields land on
      // the refresh.
      loadFields(true);
    } else if (createdMode === 'linked' && returnedLinkInfo) {
      const existingLinks = mergedIssueData?.fields?.issuelinks || [];
      const selectedLinkType = returnedLinkInfo.linkTypes?.find(t => t.id === returnedLinkInfo.linkTypeId);
      const newLink = returnedLinkInfo.direction === 'outward'
        ? {
            id: Date.now().toString(),
            type: { name: selectedLinkType?.name || 'links', inward: selectedLinkType?.inward, outward: selectedLinkType?.outward },
            outwardIssue: { key: result.key, fields: { summary: result.key, status: { name: 'To Do', statusCategory: { colorName: 'blue-gray' } }, issuetype: {} } },
          }
        : {
            id: Date.now().toString(),
            type: { name: selectedLinkType?.name || 'links', inward: selectedLinkType?.inward, outward: selectedLinkType?.outward },
            inwardIssue: { key: result.key, fields: { summary: result.key, status: { name: 'To Do', statusCategory: { colorName: 'blue-gray' } }, issuetype: {} } },
          };
      applyFieldUpdate(issueKey, { issuelinks: [...existingLinks, newLink] });
    }

    // Trigger container re-fetch so new issue appears immediately
    onIssueCreated?.();
  }, [issueKey, mergedIssueData, onFieldUpdate, applyFieldUpdate, onIssueCreated, loadFields]);

  // Link issue handler
  const handleLinkIssue = useCallback(async () => {
    if (!linkForm.targetIssueKey.trim()) {
      setLinkError('Work item key is required');
      return;
    }
    // Find "Link" type with "Child" direction automatically
    const selectedLinkType = linkTypes?.find(t => /^link$/i.test(t.name)) || linkTypes?.[0];
    if (!selectedLinkType) {
      setLinkError('Link types not loaded yet');
      return;
    }
    // Determine direction: use whichever side has "Child" label, default to outward
    const direction = (selectedLinkType.inward || '').toLowerCase().includes('child') ? 'inward' : 'outward';

    setLinkSubmitting(true);
    setLinkError(null);
    try {
      const payload = {
        type: { name: selectedLinkType.name },
      };
      if (direction === 'outward') {
        payload.inwardIssue = { key: issueKey };
        payload.outwardIssue = { key: linkForm.targetIssueKey.trim().toUpperCase() };
      } else {
        payload.inwardIssue = { key: linkForm.targetIssueKey.trim().toUpperCase() };
        payload.outwardIssue = { key: issueKey };
      }

      await api.linkIssue(payload);

      // Optimistically add to issuelinks
      {
        const existingLinks = mergedIssueData?.fields?.issuelinks || [];
        const targetKey = linkForm.targetIssueKey.trim().toUpperCase();
        const newLink = direction === 'outward'
          ? {
              id: Date.now().toString(),
              type: { name: selectedLinkType.name, inward: selectedLinkType.inward, outward: selectedLinkType.outward },
              outwardIssue: { key: targetKey, fields: { summary: targetKey, status: { name: 'Unknown', statusCategory: { colorName: 'blue-gray' } }, issuetype: {} } },
            }
          : {
              id: Date.now().toString(),
              type: { name: selectedLinkType.name, inward: selectedLinkType.inward, outward: selectedLinkType.outward },
              inwardIssue: { key: targetKey, fields: { summary: targetKey, status: { name: 'Unknown', statusCategory: { colorName: 'blue-gray' } }, issuetype: {} } },
            };
        applyFieldUpdate(issueKey, { issuelinks: [...existingLinks, newLink] });
      }

      setLinkSuccess(true);
      setLinkForm(prev => ({ ...prev, targetIssueKey: '' }));
      // Trigger container re-fetch so linked issue appears in folder view
      onIssueCreated?.();
      setTimeout(() => {
        setLinkSuccess(false);
        setShowLinkIssue(false);
      }, 2000);
    } catch (err) {
      setLinkError(extractErrorMessage(err, 'Failed to link work item'));
    } finally {
      setLinkSubmitting(false);
    }
  }, [issueKey, mergedIssueData, linkForm, linkTypes, onFieldUpdate, applyFieldUpdate, onIssueCreated]);

  // Hierarchy-aware add-relationship handler. Takes a chosen option and
  // a candidate work item key, gates via the edge's expression filters,
  // then creates the link/subtask in whichever direction the option
  // calls for.
  //
  //   option.role='child'  → current is parent, candidate is child
  //   option.role='parent' → current is child, candidate is parent
  //
  // For the gate's purposes, the edge expression always binds `issue` to
  // the child side and `parent` to the parent side, regardless of which
  // role the candidate plays.
  const handleAddRelationshipSubmit = useCallback(async (option, candidateKey, picked = null) => {
    if (!option?.edge || !candidateKey) return;
    setAddingChildError(null);
    setAddingChildSubmitting(true);
    try {
      const edge = option.edge;
      const role = option.role;
      // Determine which side of the edge is the child vs. parent.
      const childKey = role === 'parent' ? issueKey : candidateKey;
      const parentKey = role === 'parent' ? candidateKey : issueKey;

      const isSubtask = edge.relationshipType === 'subtask' || edge.relationshipType === 'child';
      // The new row wears what the picker showed — summary, type icon,
      // status — not a key-only placeholder that reads "KEY  KEY  Unknown"
      // until the panel is reopened.
      const candidateStub = pickedIssueStub(candidateKey, picked);
      if (isSubtask) {
        // Native Jira parent → set the child's parent field to point at the parent.
        await api.updateIssue(childKey, { parent: { key: parentKey } });
        if (role === 'child') {
          // Candidate becomes one of our subtasks
          const existingSubs = mergedIssueData?.fields?.subtasks || [];
          applyFieldUpdate(issueKey, { subtasks: [...existingSubs, candidateStub] });
        } else {
          // We become candidate's subtask — patch our local parent field
          applyFieldUpdate(issueKey, { parent: { key: candidateKey } });
        }
      } else {
        // Linked relationship. Edge direction = the phrase side the CHILD
        // displays. Jira POST semantics: body.inwardIssue displays the
        // OUTWARD phrase — so direction 'outward' puts the CHILD in
        // inwardIssue, direction 'inward' puts the PARENT there.
        const linkTypeName = edge.linkTypeName || LINK_TYPE_NAME;
        const linkDirection = edge.linkDirection || 'outward';
        const inwardKey = linkDirection === 'inward' ? parentKey : childKey;
        const outwardKey = linkDirection === 'inward' ? childKey : parentKey;
        const linkPromise = api.linkIssue({
          type: { name: linkTypeName },
          inwardIssue: { key: inwardKey },
          outwardIssue: { key: outwardKey },
        });
        // Optimistic patch — when fetched from "our" perspective, the
        // OTHER party sits on whichever side WE are NOT.
        const currentIsInward = inwardKey === issueKey;
        const existingLinks = mergedIssueData?.fields?.issuelinks || [];
        // The real link id (the POST returns it) so the row can be unlinked
        // straight away; the full type carries the phrases the groups read.
        const created = await linkPromise;
        const lt = linkTypes?.find(l => (l.name || '').toLowerCase() === linkTypeName.toLowerCase());
        const type = lt ? { id: lt.id, name: lt.name, inward: lt.inward, outward: lt.outward } : { name: linkTypeName };
        const createdId = created?.linkId ?? created?.id;
        const linkId = createdId != null ? String(createdId) : 'tmp-' + Date.now();
        const newLink = currentIsInward
          ? { id: linkId, type, outwardIssue: candidateStub }
          : { id: linkId, type, inwardIssue: candidateStub };
        applyFieldUpdate(issueKey, { issuelinks: [...existingLinks, newLink] });
      }

      setAddingRelationship(null);
      onIssueCreated?.();
    } catch (err) {
      setAddingChildError(extractErrorMessage(err, 'Failed to add link'));
    } finally {
      setAddingChildSubmitting(false);
    }
  }, [issueKey, mergedIssueData, linkTypes, applyFieldUpdate, onIssueCreated]);

  // Unlink issue handler
  const handleUnlinkIssue = useCallback(async (linkId) => {
    const existingLinks = mergedIssueData?.fields?.issuelinks || [];
    // Optimistically remove
    applyFieldUpdate(issueKey, { issuelinks: existingLinks.filter(l => String(l.id) !== String(linkId)) });
    try {
      await api.unlinkIssue(linkId);
    } catch {
      // Revert on error — reload from server
      loadFields(true);
    }
  }, [issueKey, mergedIssueData, applyFieldUpdate, loadFields]);

  // The effective parent: Jira's `fields.parent`, or — for a hierarchy edge
  // modelled as an issue link — the item on the parent side of that link.
  // One derivation for the Parent screen field, the Linked Work Items
  // section and the picker below, so the three never disagree.
  //
  // The parent a caller handed in wins (a tree row carries it enriched);
  // otherwise the one the full read brought back. A caller that opens the
  // panel with only a key and a summary — a list screen — would otherwise
  // show every sub-task as having no parent.
  //
  // Until THIS screen changes the parent: then the parent it read back after
  // the save (savedParent — the parent work item, or null when removed) wins
  // over both, because the caller's copy is the one from before the change —
  // the header kept showing the old parent until a refresh.
  useEffect(() => { setSavedParent(undefined); }, [issueKey]);
  const parentInfo = useMemo(() => {
    const childTypeName = issueData?.fields?.issuetype?.name || mergedIssueData?.fields?.issuetype?.name;
    // Every way the tree hangs this type — usually one; two when the same type
    // hangs by the column under one parent type and by a link under another.
    // Read as the server does (readParentOf): the column first, then a link.
    const rels = creationTree ? getRelationshipsForChild(childTypeName, creationTree, LINK_TYPE_NAME) : [];
    const callerOrRead = issueData?.fields?.parent?.key ? issueData.fields.parent : mergedIssueData?.fields?.parent;
    // (A hierarchy that hangs this type by a link keeps its parent out of
    // fields.parent — the header shows that one with the links.)
    const byLink = rels.length > 0 && rels.every(r => r.relationshipType === 'linked');
    const jiraParent = savedParent !== undefined && !byLink ? savedParent : callerOrRead;
    let linkedParent = null;
    let linkedParentLinkId = null;
    let linkedRel = null;
    for (const r of rels) {
      if (linkedParent || r.relationshipType !== 'linked') continue;
      const linkTypeName = r.linkTypeName || LINK_TYPE_NAME;
      const linkDirection = r.linkDirection || 'outward';
      for (const link of (mergedIssueData?.fields?.issuelinks || [])) {
        if ((link.type?.name || '').toLowerCase() === linkTypeName.toLowerCase()) {
          // Edge direction = the phrase side the CHILD (this issue)
          // displays; its entry keys the parent under that same side:
          // 'outward' → {outwardIssue: parent}, 'inward' → {inwardIssue: parent}.
          const parentCandidate = linkDirection === 'inward' ? link.inwardIssue : link.outwardIssue;
          if (parentCandidate) {
            linkedParent = parentCandidate;
            linkedParentLinkId = link.id;
            linkedRel = r;
            break;
          }
        }
      }
    }
    const columnRel = rels.find(r => r.relationshipType !== 'linked') || null;
    const rel = (jiraParent?.key && columnRel) || linkedRel || rels[0] || null;
    // A parent this screen just set or removed is the answer whichever way
    // the hierarchy hangs it (column or link); the link list catches up live.
    const effectiveParent = savedParent !== undefined ? savedParent : (jiraParent?.key ? jiraParent : linkedParent);
    return { jiraParent, rel, linkedParent, linkedParentLinkId, effectiveParent, hasParent: !!effectiveParent?.key };
  }, [issueData, mergedIssueData?.fields?.parent, mergedIssueData?.fields?.issuetype?.name, mergedIssueData?.fields?.issuelinks, creationTree, savedParent]);

  // The parent's own summary, type icon and status for the Parent field and
  // the header breadcrumb — Jira's parent field and a link side carry only
  // part of it.
  const [parentDetails, setParentDetails] = useState(null);
  const parentKeyForDetails = parentInfo.effectiveParent?.key || null;
  useEffect(() => {
    if (!parentKeyForDetails) { setParentDetails(null); return undefined; }
    let cancelled = false;
    api.searchIssues(`key = ${parentKeyForDetails}`, 'summary,status,issuetype', 1, siteUrl)
      .then((r) => { if (!cancelled) setParentDetails(r?.issues?.[0] || null); })
      .catch(() => { if (!cancelled) setParentDetails(null); });
    return () => { cancelled = true; };
  }, [parentKeyForDetails, siteUrl]);
  const parentWithDetails = useCallback((p) => (
    p?.key && parentDetails?.key === p.key
      ? { ...p, fields: { ...(p.fields || {}), ...parentDetails.fields } }
      : p
  ), [parentDetails]);

  // Everything the parent picker needs, shared by the screen field and the
  // Linked Work Items section. Project scope only for subtask / native-child
  // relationships (Jira's `fields.parent` requires the same project); linked
  // relationships span projects.
  // The type and project from the full read when the caller's copy lacks
  // them (a list or a grid row can carry only some fields).
  const parentEditorProps = useMemo(() => {
    const typeName = issueData?.fields?.issuetype?.name || mergedIssueData?.fields?.issuetype?.name;
    const rel = creationTree ? getRelationshipForChild(typeName, creationTree, LINK_TYPE_NAME) : null;
    // Same project only when EVERY way the type hangs is the column — a type
    // also hung by a link may take a parent from another project.
    const rels = creationTree ? getRelationshipsForChild(typeName, creationTree, LINK_TYPE_NAME) : [];
    const sameProject = !rels.length || rels.every(r => r.relationshipType === 'subtask' || r.relationshipType === 'child');
    return {
      projectKey: sameProject ? (issueData?.fields?.project?.key || mergedIssueData?.fields?.project?.key) : undefined,
      parentIssueTypes: creationTree ? getValidParentTypes(typeName, creationTree) : [],
      currentIssueKey: issueKey,
      currentJQL: creationTree ? combineJqlFilters(rel?.filters) : '',
      expression: creationTree ? combineExpressions(rel?.filters) : '',
      anchorKey: issueKey,
      excludeKeys: hierarchyDescendantKeys || [],
    };
  }, [issueData, mergedIssueData?.fields?.issuetype?.name, mergedIssueData?.fields?.project?.key, creationTree, issueKey, hierarchyDescendantKeys]);

  // Handle parent save (reparenting) — from the Parent screen field or the
  // Linked Work Items section; both open the same picker.
  const handleParentSave = useCallback(async (newParentKey) => {
    setParentError(null);
    setParentSaving(true);
    setParentSuccessMsg(null);
    try {
      // One call: the server reads the project's Architectural Hierarchy,
      // refuses a wrong type or a loop with a sentence, swaps the old parent
      // (column or hierarchy link) for the new one and records History.
      await api.setParent(issueKey, newParentKey || null);

      // The new parent as a work item (key, summary, type, status), read
      // back — what the breadcrumb and the Parent field draw. A failed read
      // still has its key.
      let fresh = newParentKey ? { key: newParentKey } : null;
      if (newParentKey) {
        try {
          const r = await api.searchIssues(`key = ${newParentKey}`, 'summary,status,issuetype', 1, siteUrl);
          if (r?.issues?.[0]?.key) fresh = { key: r.issues[0].key, fields: r.issues[0].fields || {} };
        } catch { /* the key alone */ }
      }
      setSavedParent(fresh);
      setFullIssueData(prev => (prev ? { ...prev, fields: { ...prev.fields, parent: fresh } } : prev));

      // The host hears it ONCE, as the parent's key: a grid moves the row,
      // a list patches it. It used to hear it twice — this key, then
      // { parent: { key } } through onFieldUpdate — and a host that wraps the
      // key ({ key: newParentKey }) wrapped the object too: { key: { key } },
      // drawn as text → "Objects are not valid as a React child".
      if (onParentChange) {
        onParentChange(issueKey, newParentKey || null);
      }
      // (No loadFields here: a parent change does not change the screen's
      // fields, and that reload took the slow path, jumped back to the
      // first tab and could hang on "Loading fields…".)

      // Flash success
      setSaveSuccess(prev => ({ ...prev, parent: true }));
      setTimeout(() => setSaveSuccess(prev => ({ ...prev, parent: false })), 2000);

      // Show confirmation message
      const msg = newParentKey ? `Parent updated to ${newParentKey}` : 'Parent removed';
      setParentSuccessMsg(msg);
      setTimeout(() => setParentSuccessMsg(null), 4000);

      setEditingParent(false);
      setEditingParentField(false);
    } catch (err) {
      console.error('Failed to update parent:', err);
      setParentError(err?.response?.data?.errorMessages?.[0] || err?.message || 'Failed to save parent');
    } finally {
      setParentSaving(false);
    }
  }, [issueKey, onParentChange, siteUrl]);

  // --- Mini-table helpers for insight columns on child/linked items ---
  const getEnrichedIssue = useCallback((key, fallback) => {
    return enrichedDataMap[key] || fallback;
  }, [enrichedDataMap]);

  const renderPanelCell = useCallback((issue, column) => {
    let value;
    try {
      if (column.type === 'formula') {
        // Folder-scoped formulas are per-folder metrics, not per-issue values.
        if (column.appliesTo?.mode === 'folders') return '';
        value = evalColumnFormula(issue, column, { children: [], jiraFields, fieldNameMap });
      } else {
        value = getFieldValue(issue, column.fieldId);
      }
    } catch {
      return <span style={{ color: '#e5493a', fontSize: '0.72rem' }}>Error</span>;
    }

    // Status badge
    if (column.fieldId === 'status') {
      const statusColor = issue.fields?.status?.statusCategory?.colorName;
      const catClass = statusColor === 'green' ? 'done' : statusColor === 'yellow' ? 'in-progress' : 'todo';
      return <span className={`linked-issue-status status-${catClass}`}>{value}</span>;
    }

    // Styled formula results: { text, color, fontWeight, ... }
    if (value && typeof value === 'object' && 'text' in value) {
      const style = {};
      if (value.color) style.color = value.color;
      if (value.fontWeight) style.fontWeight = value.fontWeight;
      if (value.fontSize) style.fontSize = value.fontSize;
      return <span style={style}>{formatIfCanonicalDate(value.text) ?? value.text}</span>;
    }

    if (value === null || value === undefined || value === '') {
      return <span className="panel-cell-empty">--</span>;
    }

    // A computed date wears the site's Date Format like a date field.
    return formatIfCanonicalDate(value) ?? value;
  }, [jiraFields, fieldNameMap]);

  const handlePanelCellClick = useCallback((e, issue, column) => {
    if (column.type === 'formula') return;
    const fieldId = column.fieldId;
    if (!isFieldEditable(fieldId, column)) return;
    const editorType = getEditorType(fieldId, jiraFields);
    // Text fields: inline edit in cell instead of popup
    if (editorType === 'text') {
      const currentValue = getRawFieldValue(issue, fieldId);
      setChildInlineEdit({ issueKey: issue.key, fieldId, value: currentValue || '' });
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const currentValue = getRawFieldValue(issue, fieldId);
    setChildEditingField({
      issueKey: issue.key,
      fieldId,
      editorType,
      currentValue,
      anchorRect: rect,
      issueData: issue,
    });
  }, [jiraFields]);

  // A related row's fields changed in place (its status moved from the
  // table): the enriched row, an epic's fetched child and the host all hear.
  const handleRelatedRowFields = useCallback((key, updatedFields, fallback) => {
    setEnrichedDataMap(prev => {
      const existing = prev[key] || fallback;
      if (!existing) return prev;
      return { ...prev, [key]: { ...existing, fields: { ...existing.fields, ...updatedFields } } };
    });
    setFetchedChildren(prev => prev.map(child =>
      child.key === key ? { ...child, fields: { ...child.fields, ...updatedFields } } : child
    ));
    applyFieldUpdate(key, updatedFields);
  }, [applyFieldUpdate]);

  // ✎ on the header's parent opens the editor down in Linked work items —
  // bring it into view so the click visibly does something.
  const parentEditRef = useRef(null);
  useEffect(() => {
    if (editingParent) parentEditRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [editingParent]);

  const handlePanelFieldSave = useCallback((fieldId, updatedFields) => {
    if (!childEditingField) return;
    const key = childEditingField.issueKey;
    // Update enrichedDataMap locally
    setEnrichedDataMap(prev => {
      const existing = prev[key];
      if (!existing) return prev;
      return { ...prev, [key]: { ...existing, fields: { ...existing.fields, ...updatedFields } } };
    });
    // Also update fetchedChildren if present
    setFetchedChildren(prev => prev.map(child =>
      child.key === key ? { ...child, fields: { ...child.fields, ...updatedFields } } : child
    ));
    // Propagate to parent Structure.jsx. applyFieldUpdate detects key !== issueKey
    // and skips the fullIssueData sync (that's scoped to this panel's own issue).
    applyFieldUpdate(key, updatedFields);
    setChildEditingField(null);
  }, [childEditingField, applyFieldUpdate]);

  const handlePanelFieldClose = useCallback(() => {
    setChildEditingField(null);
  }, []);

  // Inline text edit save for child/linked items
  const handleChildInlineSave = useCallback(async () => {
    if (!childInlineEdit) return;
    const { issueKey: childKey, fieldId, value, original } = childInlineEdit;
    setChildInlineEdit(null);
    // A summary renamed from its row (double-click): an untouched one is not
    // written, and none at all is refused — a work item must have a summary.
    if (fieldId === 'summary' && (!String(value || '').trim() || value === original)) return;
    try {
      const apiFields = fieldId === 'summary'
        ? { summary: value || '' }
        : { [fieldId]: value };
      await api.updateIssue(childKey, apiFields, siteUrl);
      const localUpdate = { [fieldId]: value };
      // Update enrichedDataMap
      setEnrichedDataMap(prev => {
        const existing = prev[childKey];
        if (!existing) return prev;
        return { ...prev, [childKey]: { ...existing, fields: { ...existing.fields, ...localUpdate } } };
      });
      applyFieldUpdate(childKey, localUpdate);
    } catch (err) {
      console.error('Child inline save failed:', err);
    }
  }, [childInlineEdit, siteUrl, applyFieldUpdate]);

  const handleChildInlineCancel = useCallback(() => {
    setChildInlineEdit(null);
  }, []);

  // Lazy-load comments
  const loadComments = useCallback(() => {
    if (comments !== null || commentsLoading) return;
    // The batch (getWorkItemScreen) supplies comments on success, so this
    // lazy fetch is only the fallback for the batchFailed path (Innovation
    // clients — comments stays null there). The !batchFailed gate is what
    // actually kills the duplicate: on mount the tab effect calls this
    // BEFORE the async batch resolves, when comments is still null, so the
    // comments!==null guard alone wouldn't stop the fetch. Once the batch
    // resolves it sets comments (guard returns); if it fails, batchFailed
    // flips true and this proceeds.
    if (!batchFailed) return;
    setCommentsLoading(true);
    setCommentsError(null);
    api.getComments(issueKey, siteUrl)
      .then(data => {
        setComments(data.comments || []);
      })
      .catch(err => {
        setCommentsError(extractErrorMessage(err, 'Failed to load comments'));
      })
      .finally(() => setCommentsLoading(false));
  }, [issueKey, comments, commentsLoading, siteUrl, batchFailed]);

  // Lazy-load changelog
  const loadChangelog = useCallback(() => {
    if (changelog !== null || changelogLoading) return;
    setChangelogLoading(true);
    setChangelogError(null);
    api.getChangelog(issueKey, siteUrl)
      .then(data => {
        setChangelog(data.values || []);
      })
      .catch(err => {
        setChangelogError(extractErrorMessage(err, 'Failed to load changelog'));
      })
      .finally(() => setChangelogLoading(false));
  }, [issueKey, changelog, changelogLoading, siteUrl]);

  // Load data when activity tab switches
  useEffect(() => {
    if (activityTab === 'comments') loadComments();
    if (activityTab === 'history') loadChangelog();
  }, [activityTab, loadComments, loadChangelog]);

  // A notification deep link (?comment=<id>) lands on the comment itself:
  // once the comments are in, scroll it into view — once per mount, so a
  // later refresh of the list never yanks the reader back.
  const focusedCommentDone = useRef(false);
  useEffect(() => {
    if (!focusCommentId || focusedCommentDone.current || !Array.isArray(comments)) return;
    const el = document.getElementById(`comment-${focusCommentId}`);
    if (!el) return;
    focusedCommentDone.current = true;
    setActivityTab('comments');
    requestAnimationFrame(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }, [focusCommentId, comments]);

  const [transitionScreenFor, setTransitionScreenFor] = useState(null);
  const handleTransition = useCallback(async (transitionId, transitionName, fields = null, comment = null) => {
    setTransitioningName(transitionName);
    setTransitionLoading(true);
    setTransitionError(null);
    setTransitionReasons([]);
    setStatusDropdownOpen(false);
    try {
      await api.transitionIssue(issueKey, transitionId, siteUrl, fields, comment);
      setTransitions([]); // reset so next open re-fetches
      // Notify parent to refresh the issue data with the new status
      {
        // Find the transition target to get full status category info
        const targetTransition = transitions.find(t => t.id === transitionId);
        const newStatus = targetTransition?.to
          ? { name: targetTransition.to.name, statusCategory: targetTransition.to.statusCategory }
          : { name: transitionName };
        applyFieldUpdate(issueKey, { status: newStatus });
      }
      // A transition can change the status's editability (jira.issue.editable),
      // so refresh editmeta — otherwise an item transitioned OUT of a read-only
      // status (e.g. Archive) keeps its empty edit affordances until reopened,
      // and one moved INTO a read-only status stays wrongly editable.
      api.getEditMeta(issueKey, siteUrl).then(setEditMeta).catch(() => {});
      return { ok: true };
    } catch (err) {
      console.error('Transition failed:', err);
      const data = err?.response?.data;
      // The engine returns { error, errorMessages: [<why it's blocked / what to fill in>] }.
      // Show the generic message as the headline + the specific reasons as a list.
      const reasons = Array.isArray(data?.errorMessages) ? data.errorMessages.filter(Boolean) : [];
      const errMsg = (data && data.error) || extractErrorMessage(err, 'Failed to transition status');
      setTransitionError(errMsg);
      setTransitionReasons(reasons);
      return { ok: false, error: errMsg, reasons };
    } finally {
      setTransitionLoading(false);
      setTransitioningName(null);
    }
  }, [issueKey, applyFieldUpdate, transitions, siteUrl]);

  // Add comment handler
  // A TipTap doc with no text and no media is "empty" — posting it would
  // create a blank comment nobody can delete their way out of noticing.
  const isEmptyDoc = useCallback((doc) => {
    if (!doc) return true;
    let hasSomething = false;
    const walk = (node) => {
      if (hasSomething || !node) return;
      if (node.type === 'text' && String(node.text || '').trim()) { hasSomething = true; return; }
      if (node.type && node.type !== 'doc' && node.type !== 'paragraph' && node.type !== 'text') {
        // media, mention, table, rule, … — all real content.
        hasSomething = true;
        return;
      }
      (node.content || []).forEach(walk);
    };
    walk(doc);
    return !hasSomething;
  }, []);

  // Is this work item's project a service desk? Answered for every
  // project, so a plain `false` here is a real answer and not a failure.
  useEffect(() => {
    const projectId = mergedIssueData?.fields?.project?.id;
    if (!projectId) { setIsServiceDesk(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await supportDeskApi.deskGet(projectId);
        if (alive) setIsServiceDesk(!!r?.enabled);
      } catch { if (alive) setIsServiceDesk(false); }
    })();
    return () => { alive = false; };
  }, [mergedIssueData?.fields?.project?.id]);

  // Did this work item come in through the portal, and under which form?
  // Only asked on a desk — on an ordinary project the answer is always no
  // and the call would be a round trip to learn nothing. The server decides
  // whether to answer at all: it checks the caller may browse the project,
  // so this cannot become a way to probe for desks.
  useEffect(() => {
    if (!isServiceDesk || !issueKey) { setPortalRequest(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await supportDeskApi.requestForWorkItem(issueKey);
        if (alive) setPortalRequest(r?.request || null);
      } catch { if (alive) setPortalRequest(null); }
    })();
    return () => { alive = false; };
  }, [isServiceDesk, issueKey]);

  const handleAddComment = useCallback(async () => {
    if (!newCommentText) return;
    const adfBody = tipTapToAdf(newCommentText);
    if (isEmptyDoc(adfBody)) { setAddCommentError('Write something first.'); return; }
    setAddingComment(true);
    setAddCommentError(null);
    try {
      const saved = await api.addComment(issueKey, adfBody, null, null,
        { internal: isServiceDesk && commentInternal });
      setComments(prev => [...(prev || []), saved]);
      setShowCommentEditor(false);
      setNewCommentText(null);
      clearDraft(user?.accountId, issueKey, 'comment');
    } catch (err) {
      setAddCommentError(extractErrorMessage(err, 'Failed to add comment'));
    } finally {
      setAddingComment(false);
    }
  }, [issueKey, newCommentText, isEmptyDoc, isServiceDesk, commentInternal, user?.accountId]);

  // Reply to a thread. `rootId` is always the thread ROOT — replying to a
  // reply joins the same thread rather than nesting deeper (the server
  // normalises this too, so the two can't disagree).
  const handleAddReply = useCallback(async (rootId) => {
    if (!replyText) return;
    const adfBody = tipTapToAdf(replyText);
    if (isEmptyDoc(adfBody)) { setAddCommentError('Write something first.'); return; }
    setAddingComment(true);
    setAddCommentError(null);
    try {
      const saved = await api.addComment(issueKey, adfBody, null, rootId);
      setComments(prev => [...(prev || []), saved]);
      setReplyingTo(null);
      setReplyText(null);
      clearDraft(user?.accountId, issueKey, `reply:${rootId}`);
    } catch (err) {
      setAddCommentError(extractErrorMessage(err, 'Failed to add reply'));
    } finally {
      setAddingComment(false);
    }
  }, [issueKey, replyText, isEmptyDoc, user?.accountId]);

  const handleDeleteComment = useCallback(async (comment) => {
    setDeletingCommentBusy(true);
    setAddCommentError(null);
    try {
      await api.deleteComment(issueKey, comment.id);
      // A thread root takes its replies with it server-side; mirror that here
      // so the list doesn't briefly show orphans.
      setComments(prev => (prev || []).filter(c =>
        c.id !== comment.id && c.parentId !== comment.id));
      setDeletingComment(null);
      if (replyingTo === comment.id) { setReplyingTo(null); setReplyText(null); }
    } catch (err) {
      setAddCommentError(extractErrorMessage(err, 'Failed to delete comment'));
      setDeletingComment(null);
    } finally {
      setDeletingCommentBusy(false);
    }
  }, [issueKey, replyingTo]);

  // Threads: roots in creation order, each with its replies in creation order.
  // Only the ROOT order flips with the sort toggle — replies always read
  // oldest-first, the way a conversation is meant to.
  const commentThreads = useMemo(() => {
    if (!comments) return [];
    const byTime = (a, b) => new Date(a.created || 0) - new Date(b.created || 0);
    const roots = comments.filter(c => !c.parentId).sort(byTime);
    const repliesByRoot = new Map();
    for (const c of comments) {
      if (!c.parentId) continue;
      if (!repliesByRoot.has(c.parentId)) repliesByRoot.set(c.parentId, []);
      repliesByRoot.get(c.parentId).push(c);
    }
    // A reply whose root was deleted out from under us (another user, another
    // tab) would otherwise vanish silently — surface it as its own thread.
    const rootIds = new Set(roots.map(c => c.id));
    const orphans = comments.filter(c => c.parentId && !rootIds.has(c.parentId)).sort(byTime);
    const ordered = [...roots, ...orphans].sort(byTime);
    if (commentSortOrder === 'desc') ordered.reverse();
    return ordered.map(root => ({
      root,
      replies: (repliesByRoot.get(root.id) || []).sort(byTime),
    }));
  }, [comments, commentSortOrder]);

  const commentCount = comments ? comments.length : 0;

  // Attachment upload handler — any number of File objects, one after another.
  // Several at once: the Upload file picker takes a multi-selection and a drop
  // brings every file (2026-10-22: "I can upload as many attachments as I like
  // onto a field but … Upload file at the bottom of a work item only one at a
  // time"). Each upload ADDS to the list as it stands after the one before:
  // every call used to start from the list this render saw, so of a dropped
  // handful only the last showed until a refresh. A file that fails does not
  // stop the rest.
  const handleFilesUpload = useCallback(async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploadingAttachment(true);
    let current = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
    try {
      for (const file of list) {
        try {
          const result = await api.uploadAttachment(issueKey, file);
          if (result?.[0]) {
            current = [...current, result[0]];
            setFetchedAttachments(current);
            applyFieldUpdate(issueKey, { attachment: current });
          }
        } catch (err) {
          console.error(`Upload failed (${file.name}):`, err);
        }
      }
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  }, [issueKey, mergedIssueData?.fields?.attachment, fetchedAttachments, applyFieldUpdate]);
  const handleFileUpload = useCallback((file) => handleFilesUpload([file]), [handleFilesUpload]);

  // Delete attachment handler
  const handleDeleteAttachment = useCallback(async (attachmentId) => {
    if (!confirm('Delete this attachment?')) return;
    try {
      // Resolve the attachment's UUID before deleting — once it's
      // gone we can't look it up. The UUID is what ADF media nodes
      // reference, so we need it to strip those nodes.
      const existingAttachments = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
      const target = existingAttachments.find(a => String(a.id) === String(attachmentId));
      const mediaUuid = target?.mediaApiFileId || null;

      await api.deleteAttachment(attachmentId);

      // Update the panel's attachment list straight away.
      const updated = existingAttachments.filter(a => String(a.id) !== String(attachmentId));
      setFetchedAttachments(updated);
      applyFieldUpdate(issueKey, { attachment: updated });

      // Strip the deleted media from any rich-text (ADF) field that
      // referenced it. We skip 'attachment'/'comment' (panel and
      // comments-list, both handled separately) and the field that
      // is currently being edited (would clobber unsaved edits).
      const fields = mergedIssueData?.fields || {};
      for (const fieldKey of Object.keys(fields)) {
        if (fieldKey === 'attachment' || fieldKey === 'comment') continue;
        if (fieldKey === editingFieldId) continue;
        const value = fields[fieldKey];
        if (!value || typeof value !== 'object' || value.type !== 'doc') continue;

        const { doc: cleaned, changed } = stripMediaFromAdf(value, mediaUuid, String(attachmentId));
        if (!changed) continue;
        try {
          await api.updateIssue(issueKey, { [fieldKey]: cleaned }, siteUrl);
          applyFieldUpdate(issueKey, { [fieldKey]: cleaned });
        } catch (err) {
          // Best-effort: if a single field save fails (permissions,
          // edit-meta restriction, etc.), log and keep going. The
          // attachment is already gone from Jira so the user has at
          // worst a stale tile in that one field — which now also
          // 404s on click since the new graceful DELETE will clean
          // it up the next time they try.
          console.warn(`[Attachment] Field strip failed for "${fieldKey}":`, err.message);
        }
      }
    } catch (err) {
      // Say so — a silent failure reads as "the delete did nothing".
      console.error('Delete attachment failed:', err);
      alert(extractErrorMessage(err, 'The attachment could not be deleted.'));
    }
  }, [issueKey, mergedIssueData?.fields, fetchedAttachments, applyFieldUpdate, editingFieldId, siteUrl]);

  // Panel-level drag-and-drop handlers
  const handlePanelDragEnter = useCallback((e) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    panelDragCounterRef.current++;
    setPanelDragActive(true);
  }, []);

  const handlePanelDragLeave = useCallback((e) => {
    e.preventDefault();
    panelDragCounterRef.current--;
    if (panelDragCounterRef.current <= 0) {
      panelDragCounterRef.current = 0;
      setPanelDragActive(false);
    }
  }, []);

  const handlePanelDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handlePanelDrop = useCallback(async (e) => {
    e.preventDefault();
    panelDragCounterRef.current = 0;
    setPanelDragActive(false);
    await handleFilesUpload(e.dataTransfer.files);
  }, [handleFilesUpload]);

  // Configure deep-links into Settings → Workflows & Screens → Screen Layout
  // with this work item's project + work type pre-selected, so the layout that
  // opens is the one you were just looking at.
  //
  // It used to point at /stride-migration — the standalone migration console,
  // which is a different page with its own chrome and run pickers. Screen
  // Layout is a normal settings section; that is where it belongs.
  const configureUrl = useMemo(() => {
    const projectId = mergedIssueData?.fields?.project?.id;
    const issueTypeId = mergedIssueData?.fields?.issuetype?.id;
    if (!projectId || !issueTypeId) return null;
    return `/settings/screen-layout?projectId=${encodeURIComponent(projectId)}&issueTypeId=${encodeURIComponent(issueTypeId)}`;
  }, [mergedIssueData?.fields?.project?.id, mergedIssueData?.fields?.issuetype?.id]);

  // Build field data: prioritize issue layout -> screen layout -> editmeta
  const tabs = useMemo(() => {
    const editMetaFields = editMeta?.fields || {};
    // A status property (jira.issue.editable / jira.permission.edit.group)
    // keeps this user from editing: `fields` is empty, and `viewFields` carries
    // the same screen's fields for their TYPE only — so every field draws as
    // it does for an editor, just without being editable.
    const statusLocked = !!editMeta?.statusLocked;
    const viewFields = editMeta?.viewFields || {};
    // A field a site's own rule locks (editmeta `locked`: fieldId → the rule's words, see
    // work-items/server/field-locks.js) is out of `fields` and in `viewFields` — drawn as a
    // status-locked field is (the same box, its type's display: a date as a date, never opening),
    // with a lock mark beside its label whose hover text is the rule's words.
    const fieldLocks = editMeta?.locked || {};

    // Time fields are editable via the timetracking wrapper even though
    // Jira's editmeta doesn't list them directly
    const TIME_EDITABLE_FIELDS = new Set(['timeoriginalestimate', 'timeestimate']);

    const buildField = (fieldKey, fieldName) => {
      const meta = editMetaFields[fieldKey];
      const shape = meta || viewFields[fieldKey];
      const jiraField = jiraFields.find(f => f.id === fieldKey || f.key === fieldKey);
      return {
        key: fieldKey,
        name: FIELD_NAME_OVERRIDES[fieldKey] || shape?.name || fieldName || jiraField?.name || fieldKey,
        required: meta?.required || false,
        schema: shape?.schema,
        allowedValues: meta?.allowedValues,
        // No editmeta (a viewer without edit rights) still knows a Line items
        // field by its type, so its table shows — read-only — not a text summary.
        // With a schema in hand (editmeta, or the view field when the status
        // locks the item) that schema decides; with neither, fall back to the
        // field catalogue rather than guessing from the key — see screenEditorType.
        editorType: shape ? getSchemaEditorType(fieldKey, shape.schema)
          : resolveScreenEditorType(fieldKey, meta, jiraField),
        hasEditMeta: !!meta || (!statusLocked && TIME_EDITABLE_FIELDS.has(fieldKey)),
        statusLocked: (statusLocked && !!viewFields[fieldKey]) || !!fieldLocks[fieldKey],
        fieldLock: fieldLocks[fieldKey] || null,
      };
    };

    // Build a section lookup from issue layout (content vs primary/secondary),
    // and each Details field's place in the layout's list (sectionPos: the
    // Details panel draws the layout's order — see ./screenSections.js)
    const sectionMap = {};
    const sectionPos = {};
    if (issueLayout?.sections) {
      const { content, primary, secondary } = issueLayout.sections;
      for (const f of (content || [])) sectionMap[f.key] = 'content';
      (primary || []).forEach((f, i) => { sectionMap[f.key] = 'primary'; sectionPos[f.key] = i; });
      (secondary || []).forEach((f, i) => { sectionMap[f.key] = 'secondary'; sectionPos[f.key] = i; });
    }
    const hasSections = Object.keys(sectionMap).length > 0;

    // With a cached Jira layout, the layout decides. Without one, synthesise
    // the split (defaultSectionType) — but only if at least one field actually
    // belongs in the sidebar. A screen with none would otherwise render an
    // empty "Details" rail next to the fields, so those keep the flat
    // single-column form (sectionType stays null throughout).
    const applySections = (tabList) => {
      if (hasSections) return tabList;
      const anyDetails = tabList.some(t => t.fields.some(f => defaultSectionType(f) === 'primary'));
      if (!anyDetails) return tabList;
      return tabList.map(t => ({
        ...t,
        fields: t.fields.map(f => ({ ...f, sectionType: defaultSectionType(f) })),
      }));
    };

    // Priority 1: Screen layout tabs + issue layout sections (best of both)
    // Tabs from screen layout, section assignment from issue layout
    // Also merge in any issue layout fields not present in the screen layout
    if (screenLayout?.tabs?.length > 0) {
      const screenFieldKeys = new Set();
      const result = screenLayout.tabs.map(tab => ({
        id: tab.id,
        name: tab.name,
        fields: tab.fields
          .filter(f => !SKIP_FIELDS.has(f.id))
          .map(f => {
            screenFieldKeys.add(f.id);
            return {
              ...buildField(f.id, f.name),
              sectionType: sectionMap[f.id] || null,
              sectionPos: sectionPos[f.id],
            };
          }),
      }));

      // Note: we intentionally do NOT merge extra fields from the issue layout.
      // The screen layout (from the shared screen scheme) is the source of truth
      // for which fields to show. The issue layout is only used for section
      // positioning (content vs primary vs secondary) of screen layout fields.

      return applySections(result);
    }

    // Priority 2: Issue layout only (no screen layout tabs available)
    if (hasSections) {
      const allKeys = [...(issueLayout.sections.content || []),
                       ...(issueLayout.sections.primary || []),
                       ...(issueLayout.sections.secondary || [])];
      const fields = allKeys
        .filter(f => !SKIP_FIELDS.has(f.key))
        .map(f => ({
          ...buildField(f.key, f.name),
          sectionType: sectionMap[f.key] || null,
          sectionPos: sectionPos[f.key],
        }));
      return [{ id: 'layout', name: 'Fields', fields }];
    }

    // Priority 3: Editmeta only — the path a Stride-created project takes when
    // it has neither a screen layout nor a cached Jira issue layout. Sectioned
    // with the same synthesised default so it still renders two columns.
    // (A rule-locked field comes from viewFields here too, drawn read-only.)
    const metaOnly = statusLocked ? viewFields : { ...editMetaFields, ...viewFields };
    if (Object.keys(metaOnly).length > 0) {
      const fields = Object.entries(metaOnly)
        .filter(([key]) => !SKIP_FIELDS.has(key))
        .map(([key, meta]) => {
          const jiraField = jiraFields.find(f => f.id === key || f.key === key);
          const readOnly = statusLocked || !!fieldLocks[key];
          return {
            key,
            name: meta.name || jiraField?.name || key,
            required: readOnly ? false : meta.required,
            schema: meta.schema,
            allowedValues: readOnly ? undefined : meta.allowedValues,
            editorType: getSchemaEditorType(key, meta.schema),
            hasEditMeta: !readOnly,
            statusLocked: readOnly,
            fieldLock: fieldLocks[key] || null,
            sectionType: null,
          };
        });

      return applySections([{ id: 'default', name: 'Fields', fields }]);
    }

    return [];
  }, [issueLayout, screenLayout, editMeta, jiraFields]);

  const handleFieldClick = useCallback((field) => {
    if (READONLY_TYPES.has(field.editorType)) return;
    if (!field.hasEditMeta) return; // Can't edit without editmeta

    const currentValue = mergedIssueData?.fields?.[field.key];
    setEditingFieldId(field.key);
    editDirtyRef.current = false;

    if (field.editorType === 'rich-text') {
      // Text typed here before and not saved (unsavedDrafts.js) — the box
      // opens on it, Save ready, as Jira's does.
      const draft = readDraft(user?.accountId, issueKey, `field:${field.key}`);
      if (draft) {
        editDirtyRef.current = true;
        setEditValue(draft.doc);
        return;
      }
      const converted = adfToTipTap(currentValue);
      // Resolve wiki-image:// and jira-media:// to proxy URLs before edit mode mounts
      const allAtt = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
      resolveImageRefs(converted, allAtt, mediaMap);
      setEditValue(converted);
    } else {
      setEditValue(currentValue ?? '');
    }

    // Fetch options for multiselect fields if not already in allowedValues
    if (field.editorType === 'multiselect-display' && !field.allowedValues) {
      setMultiselectOptions(null);
      const projectKey = mergedIssueData?.fields?.project?.key;
      const issueTypeId = mergedIssueData?.fields?.issuetype?.id;
      api.getFieldOptions(field.key, projectKey, issueTypeId, issueKey)
        .then(result => {
          const opts = Array.isArray(result) ? result : (result?.values || result?.allowedValues || result || []);
          setMultiselectOptions(opts);
        })
        .catch(() => setMultiselectOptions([]));
    }
  }, [mergedIssueData, issueKey, user?.accountId]);

  const handleSave = useCallback(async (fieldKey, value, editorType, opts = {}) => {
    const saveValue = editorType === 'rich-text' ? tipTapToAdf(value) : value;
    const apiFields = formatFieldForApi(fieldKey, saveValue, editorType);
    // apiFields strips objects to just { id } / { accountId } — use the full
    // value for the local display update.
    const displayFields = { [fieldKey]: saveValue };
    // Snapshot the current value so a failed save can roll the UI back.
    const prevValue = mergedIssueData?.fields?.[fieldKey];

    // OPTIMISTIC: paint the new value, exit edit mode, and flash green
    // IMMEDIATELY so the field reacts the instant the user clicks — instead
    // of waiting for the PUT round-trip (which is why non-date fields lagged;
    // a date input only *felt* instant because its open input echoes the
    // typed value). The save then runs in the background; only a failure
    // reverts the optimistic paint and surfaces the error.
    // opts.keepOpen keeps the editor open (multi-select: each tick saves but
    // the dropdown stays open until the user clicks away / another field).
    setSaveErrors(prev => ({ ...prev, [fieldKey]: null }));
    applyFieldUpdate(issueKey, displayFields);
    if (!opts.keepOpen) setEditingFieldId(null);
    if (editorType === 'rich-text') clearDraft(user?.accountId, issueKey, `field:${fieldKey}`);
    setSaveSuccess(prev => ({ ...prev, [fieldKey]: true }));
    setTimeout(() => {
      setSaveSuccess(prev => ({ ...prev, [fieldKey]: false }));
    }, 2000);

    setSaving(prev => ({ ...prev, [fieldKey]: true }));
    try {
      await api.updateIssue(issueKey, apiFields, siteUrl);
    } catch (err) {
      // Roll back the optimistic paint + surface the error.
      applyFieldUpdate(issueKey, { [fieldKey]: prevValue });
      // The editor closed on the click: what was typed waits as a draft again.
      if (editorType === 'rich-text') writeDraft(user?.accountId, issueKey, `field:${fieldKey}`, { doc: value });
      setSaveSuccess(prev => ({ ...prev, [fieldKey]: false }));
      setSaveErrors(prev => ({ ...prev, [fieldKey]: extractErrorMessage(err, 'Failed to save') }));
    } finally {
      setSaving(prev => ({ ...prev, [fieldKey]: false }));
    }
  }, [issueKey, applyFieldUpdate, mergedIssueData, user?.accountId]);

  // ---- Security level, from the header lock -------------------------------
  // Same optimistic shape as handleSave, but the value is a level from the
  // project's scheme ({ id, name }) or null to lift the restriction. The
  // server refuses (400) a level outside the project's scheme and (403) a
  // caller without SET_ISSUE_SECURITY; either reverts the paint.
  const handleSecurityChange = useCallback(async (level) => {
    setSecurityMenuOpen(false);
    const prev = mergedIssueData?.fields?.security ?? null;
    const next = level ? { id: String(level.id), name: level.name } : null;
    if ((prev?.id || null) === (next?.id || null)) return;
    setSaveErrors(p => ({ ...p, security: null }));
    applyFieldUpdate(issueKey, { security: next });
    setSecuritySaving(true);
    try {
      await api.updateIssue(issueKey, { security: next }, siteUrl);
    } catch (err) {
      applyFieldUpdate(issueKey, { security: prev });
      setSaveErrors(p => ({ ...p, security: extractErrorMessage(err, 'Failed to set the security level') }));
    } finally {
      setSecuritySaving(false);
    }
  }, [issueKey, siteUrl, applyFieldUpdate, mergedIssueData]);

  useEffect(() => {
    if (!securityMenuOpen) return;
    const onDown = (e) => {
      if (securityMenuRef.current && !securityMenuRef.current.contains(e.target)) setSecurityMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [securityMenuOpen]);

  const handleEditKeyDown = useCallback((e, fieldKey, editorType) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave(fieldKey, editValue, editorType);
    }
    if (e.key === 'Escape') {
      editDirtyRef.current = false;
      setEditingFieldId(null);
    }
  }, [editValue, handleSave]);

  // ---- Summary, edited in place from the panel title ---------------------
  // The summary field is deliberately absent from the field list (SKIP_FIELDS)
  // because the title already shows it. Double-clicking the title is the way
  // to change it — same optimistic save path every other field uses.
  const summaryEditable = !!editMeta?.fields?.summary;

  const beginSummaryEdit = useCallback(() => {
    if (!summaryEditable) return;
    setSummaryDraft(mergedIssueData?.fields?.summary || '');
    setEditingSummary(true);
  }, [summaryEditable, mergedIssueData?.fields?.summary]);

  const commitSummaryEdit = useCallback(() => {
    if (!editingSummary) return;
    setEditingSummary(false);
    const next = summaryDraft.trim();
    const current = mergedIssueData?.fields?.summary || '';
    // Empty is not a valid summary in Jira — treat a cleared box as a cancel.
    if (!next || next === current) return;
    handleSave('summary', next, 'text');
  }, [editingSummary, summaryDraft, mergedIssueData?.fields?.summary, handleSave]);

  const handleSummaryKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitSummaryEdit(); }
    if (e.key === 'Escape') { e.preventDefault(); setEditingSummary(false); }
  }, [commitSummaryEdit]);

  // Focus + select the whole summary the moment the editor opens.
  useEffect(() => {
    if (!editingSummary) return;
    const el = summaryInputRef.current;
    if (el) { el.focus(); el.select(); }
  }, [editingSummary]);

  // Switching to another work item must not carry an open summary editor over.
  useEffect(() => { setEditingSummary(false); }, [issueKey]);

  // a file a field's own view attached (a whiteboard drawing, a fork's document) shows under
  // Attachments at once
  const addAttachment = (att) => {
    if (!att) return;
    const list = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
    if (list.some((a) => String(a.id) === String(att.id))) return;
    const next = [...list, att];
    setFetchedAttachments(next);
    applyFieldUpdate(issueKey, { attachment: next });
  };

  const renderFieldValue = (field) => {
    const value = mergedIssueData?.fields?.[field.key];
    const isEditing = editingFieldId === field.key;
    const isSaving = saving[field.key];
    const hasSuccess = saveSuccess[field.key];
    const fieldError = saveErrors[field.key];
    const isEditable = field.hasEditMeta && !READONLY_TYPES.has(field.editorType);

    // A client fork's own view of this field (FIELD_VIEWS in its client config — ../FieldView/fieldViews):
    // it takes the value's place and saves itself, so there is no click-to-edit wrapper around it.
    const ForkView = fieldViewFor(field);
    if (ForkView) {
      return (
        <div className="screen-field-value" style={{ display: 'block' }}>
          <FieldViewHost key={`${issueKey}:${field.key}`} View={ForkView} issueKey={issueKey} fieldId={field.key} fieldName={field.name} value={value} editable={isEditable}
            onFieldsChanged={(fields) => applyFieldUpdate(issueKey, fields || {})} onAttachmentAdded={addAttachment} />
        </div>
      );
    }

    // Line items: the table edits itself line by line (its own routes, its own
    // permission check), so there is no click-to-edit wrapper around it.
    if (field.editorType === 'lineitems') {
      return (
        <div className="screen-field-value" style={{ display: 'block' }}>
          <LineItemsField issueKey={issueKey} fieldId={field.key} onWorkItemStatus={(status) => { applyFieldUpdate(issueKey, { status }); setTransitions([]); }} />
        </div>
      );
    }

    // Whiteboard: the board saves itself, element by element (its own routes,
    // its own permission check, live with everyone who has it open).
    if (field.editorType === 'whiteboard') {
      return (
        <div className="screen-field-value" style={{ display: 'block' }}>
          <WhiteboardField issueKey={issueKey} fieldId={field.key} fieldName={field.name}
            onAttachmentAdded={addAttachment} />
        </div>
      );
    }

    // Document: the work item drawn as a document — every field, the Approval
    // Log, a page per attachment, a PDF (modules/database/document-field). It
    // stores no value; its settings are the field's own.
    if (field.editorType === 'document') {
      return (
        <div className="screen-field-value" style={{ display: 'block' }}>
          <Suspense fallback={<div style={{ fontSize: 13, opacity: 0.7, padding: '10px 0' }}>Loading…</div>}>
            <DocumentField issueKey={issueKey} fieldId={field.key} fieldName={field.name} />
          </Suspense>
        </div>
      );
    }

    // Work item reference: a read-only card of the work item the value names
    // (automations set it), its key opening that item the way a row's does.
    if (field.editorType === 'workitemref') {
      return (
        <div className="screen-field-value readonly" style={{ display: 'block' }}>
          <WorkItemRefField fieldId={field.key} value={value} onOpenIssue={handleIssueKeyClick} />
        </div>
      );
    }

    // Status badge
    if (field.editorType === 'status-badge') {
      const statusCategory = value?.statusCategory?.colorName;
      const categoryClass = statusCategory === 'green' ? 'done' :
                           statusCategory === 'yellow' ? 'in-progress' : 'todo';
      return (
        <div className="screen-field-value">
          <span className={`status-badge status-${categoryClass}`}>
            {value?.name || '—'}
          </span>
        </div>
      );
    }

    // Rich text editor (description)
    if (field.editorType === 'rich-text') {
      if (isEditing) {
        return (
          <div className="rich-text-edit-wrapper">
            <RichTextEditor
              key={`${field.key}-edit`}
              value={editValue}
              onChange={(json) => {
                editDirtyRef.current = true;
                setEditValue(json);
                writeDraft(user?.accountId, issueKey, `field:${field.key}`, { doc: json });
              }}
              disabled={isSaving}
              // Attach/drop/paste in the editor is CREATE_ATTACHMENTS too:
              // no hook when the scheme withholds it, and the editor
              // hides its affordances.
              onFileUpload={!attachmentPerms.create ? undefined : async (file) => {
                const result = await api.uploadAttachment(issueKey, file);
                const attachment = result[0];
                // Mirror the new attachment into the panel's local
                // attachment list so the dropdown's attachments
                // section reflects the upload immediately, without
                // waiting for the webhook + mirror sync round-trip.
                const existing = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
                const updated = [...existing, attachment];
                setFetchedAttachments(updated);
                applyFieldUpdate(issueKey, { attachment: updated });
                return {
                  ...attachment,
                  content: api.getAttachmentContentUrl(attachment.id),
                  mediaApiFileId: attachment.mediaApiFileId || null,
                };
              }}
            />
            <div className="rich-text-edit-actions">
              <button
                type="button"
                className="rich-text-save-btn"
                disabled={isSaving || !editDirtyRef.current}
                onClick={() => handleSave(field.key, editValue, field.editorType)}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="rich-text-cancel-btn"
                disabled={isSaving}
                onClick={() => {
                  const doDiscard = () => {
                    editDirtyRef.current = false;
                    setEditingFieldId(null);
                    clearDraft(user?.accountId, issueKey, `field:${field.key}`);
                    setFieldError(null);
                  };
                  if (editDirtyRef.current) {
                    setDiscardConfirm(() => doDiscard);
                    return;
                  }
                  doDiscard();
                }}
              >
                Cancel
              </button>
            </div>
            {fieldError && <div className="screen-field-error">{fieldError}</div>}
          </div>
        );
      }

      // Read mode: promote legacy Jira wiki-markup (h2., bq.) inside text
      // nodes to proper ADF heading/blockquote nodes BEFORE conversion so the
      // TipTap read-only renderer styles them correctly. Edit mode skips this
      // so the user sees and edits the raw stored ADF.
      const tipTapContent = adfToTipTap(preprocessAdfWikiMarkup(value));
      // Resolve image references (jira-media:// and wiki-image://) to proxy URLs
      const allAttachments = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
      resolveImageRefs(tipTapContent, allAttachments, mediaMap);
      const hasContent = value && (typeof value === 'string' ? value.trim() : value.content?.length > 0);
      // Key must change when attachments or media map loads — TipTap only uses content on mount —
      // and when the CONTENT changes: a value written by something else on the screen (a field
      // view's save) was never drawn until a reload (utils/contentKey.js)
      const editorKey = `${field.key}-${typeof value}-${allAttachments.length}-${mediaMap ? 'm' : ''}-${contentFingerprint(value)}`;
      return (
        <div
          className={`screen-field-value rich-text-readonly ${field.hasEditMeta ? 'editable' : field.statusLocked ? 'status-locked' : ''} ${hasSuccess ? 'save-flash' : ''}`}
          onClick={field.hasEditMeta ? () => handleFieldClick(field) : undefined}
        >
          {hasContent ? (
            <RichTextEditor key={editorKey} value={tipTapContent} readOnly />
          ) : (
            <span>—</span>
          )}
          {field.hasEditMeta && <span className="screen-field-edit-icon">&#9998;</span>}
          {fieldError && <div className="screen-field-error">{fieldError}</div>}
        </div>
      );
    }

    // Parent — Jira lets you change it from the field itself, and so does
    // Stride: the same picker and the same save the Linked Work Items
    // section runs, opened in place. Shows the EFFECTIVE parent (a
    // link-modelled hierarchy edge counts), not only Jira's fields.parent.
    // Editable when the edit screen carries the field or a hierarchy is
    // configured — the permission scheme still has the last word on save.
    if (field.key === 'parent') {
      const { effectiveParent } = parentInfo;
      const parentLocked = !!editMeta?.statusLocked;
      const canEditParent = !parentLocked && (field.hasEditMeta || !!creationTree);
      const parentFlash = saveSuccess.parent;
      if (editingParentField) {
        return (
          <div className="screen-field-edit screen-field-edit-parent">
            <ParentEditor
              value={effectiveParent?.key || null}
              disabled={parentSaving}
              onAutoSave={handleParentSave}
              {...parentEditorProps}
            />
            <button className="screen-panel-cancel-btn" onClick={() => { setEditingParentField(false); setParentError(null); }} disabled={parentSaving}>Cancel</button>
            {parentSaving && <div className="parent-loading-msg">Saving parent...</div>}
            {parentError && <div className="screen-field-error">{parentError}</div>}
          </div>
        );
      }
      return (
        <div
          className={`screen-field-value ${canEditParent ? 'editable' : parentLocked && creationTree ? 'readonly status-locked' : 'readonly'} ${parentFlash ? 'save-flash' : ''}`}
          onClick={canEditParent ? (e) => {
            // A click on the key itself follows the link; the rest of the
            // field opens the picker.
            if (e.target.closest('a')) return;
            setParentError(null);
            setEditingParentField(true);
          } : undefined}
        >
          {formatDisplayValue('parent', parentWithDetails(effectiveParent), field.editorType, { adminMode, onIssueKeyClick: handleIssueKeyClick })}
          {canEditParent && <span className="screen-field-edit-icon">&#9998;</span>}
          {parentSuccessMsg && !parentSaving && <div className="parent-success-msg">{parentSuccessMsg}</div>}
        </div>
      );
    }

    // Read-only display. A field only a status property keeps from this user
    // still wears its box (status-locked), so the screen looks the same as
    // it does for someone who may edit — it just does not open.
    if (READONLY_TYPES.has(field.editorType) || !field.hasEditMeta) {
      const boxed = field.statusLocked && !READONLY_TYPES.has(field.editorType);
      return (
        <div className={`screen-field-value readonly${boxed ? ' status-locked' : ''}`}>
          {formatDisplayValue(field.key, value, field.editorType, { adminMode, onIssueKeyClick: handleIssueKeyClick })}
        </div>
      );
    }

    // User display — show as editable with user picker
    if (field.editorType === 'user-display') {
      if (isEditing) {
        return (
          <div className="screen-field-edit screen-field-edit-user">
            <UserEditor
              value={editValue}
              onChange={(user) => {}}
              onSearchUsers={String(field.key).startsWith('customfield_')
                // A custom user field lists only its context's allowed users.
                ? (q) => searchUsers(q, { fieldId: field.key, projectKey: mergedIssueData?.fields?.project?.key, issueTypeId: mergedIssueData?.fields?.issuetype?.id, issueKey })
                : searchUsers}
              disabled={isSaving}
              onAutoSave={(user) => {
                const formatted = user ? { accountId: user.accountId, displayName: user.displayName, avatarUrls: user.avatarUrls } : null;
                handleSave(field.key, formatted, field.editorType);
              }}
            />
            <button className="screen-edit-btn cancel screen-user-cancel" onClick={() => setEditingFieldId(null)} disabled={isSaving} title="Cancel">✕</button>
            {fieldError && <div className="screen-field-error">{fieldError}</div>}
          </div>
        );
      }
      return (
        <>
          <div
            className={`screen-field-value ${field.hasEditMeta ? 'editable' : ''} ${hasSuccess ? 'save-flash' : ''}`}
            onClick={field.hasEditMeta ? () => handleFieldClick(field) : undefined}
          >
            {formatDisplayValue(field.key, value, field.editorType, { adminMode, onIssueKeyClick: handleIssueKeyClick })}
            {field.hasEditMeta && <span className="screen-field-edit-icon">&#9998;</span>}
          </div>
          {fieldError && <div className="screen-field-error">{fieldError}</div>}
        </>
      );
    }

    // Multi-user display — several people, added and removed in place; saved
    // after a short pause like the multi-select, and closed on a click away.
    if (field.editorType === 'multiuser-display') {
      if (isEditing) {
        return (
          <div className="screen-field-edit screen-field-edit-user">
            <MultiUserEditor
              value={editValue}
              onChange={(people) => {
                setEditValue(people);
                msPendingRef.current = true;
                clearTimeout(window._muAutoSaveTimer);
                window._muAutoSaveTimer = setTimeout(() => {
                  msPendingRef.current = false;
                  handleSave(field.key, people, field.editorType, { keepOpen: true });
                }, 800);
              }}
              onRequestClose={() => {
                clearTimeout(window._muAutoSaveTimer);
                if (msPendingRef.current) {
                  msPendingRef.current = false;
                  handleSave(field.key, editValue, field.editorType);
                } else {
                  setEditingFieldId(null);
                }
              }}
              onSearchUsers={(q) => searchUsers(q, { fieldId: field.key, projectKey: mergedIssueData?.fields?.project?.key, issueTypeId: mergedIssueData?.fields?.issuetype?.id, issueKey })}
              disabled={isSaving}
            />
            {fieldError && <div className="screen-field-error">{fieldError}</div>}
          </div>
        );
      }
      return (
        <>
          <div
            className={`screen-field-value ${field.hasEditMeta ? 'editable' : ''} ${hasSuccess ? 'save-flash' : ''}`}
            onClick={field.hasEditMeta ? () => handleFieldClick(field) : undefined}
          >
            {formatDisplayValue(field.key, value, field.editorType, { adminMode, onIssueKeyClick: handleIssueKeyClick })}
            {field.hasEditMeta && <span className="screen-field-edit-icon">&#9998;</span>}
          </div>
          {fieldError && <div className="screen-field-error">{fieldError}</div>}
        </>
      );
    }

    // Multi-select display — show as editable with multi-select picker
    if (field.editorType === 'multiselect-display') {
      if (isEditing) {
        const msOptions = field.allowedValues || multiselectOptions;
        return (
          <div className="screen-field-edit screen-field-edit-user">
            <MultiSelectEditor
              value={editValue}
              onChange={(val) => {
                setEditValue(val);
                // Save after a short debounce (batches rapid ticks) but KEEP
                // the dropdown open — it closes only on click-away / field switch.
                msPendingRef.current = true;
                clearTimeout(window._msAutoSaveTimer);
                window._msAutoSaveTimer = setTimeout(() => {
                  msPendingRef.current = false;
                  handleSave(field.key, val, field.editorType, { keepOpen: true });
                }, 800);
              }}
              onRequestClose={() => {
                // Clicked outside the dropdown → flush a pending tick (if any)
                // and close; skip a redundant save when nothing is pending.
                clearTimeout(window._msAutoSaveTimer);
                if (msPendingRef.current) {
                  msPendingRef.current = false;
                  handleSave(field.key, editValue, field.editorType);
                } else {
                  setEditingFieldId(null);
                }
              }}
              options={msOptions || []}
              loading={!msOptions}
            />
            {fieldError && <div className="screen-field-error">{fieldError}</div>}
          </div>
        );
      }
      return (
        <>
          <div
            className={`screen-field-value ${field.hasEditMeta ? 'editable' : ''} ${hasSuccess ? 'save-flash' : ''}`}
            onClick={field.hasEditMeta ? () => handleFieldClick(field) : undefined}
          >
            {formatDisplayValue(field.key, value, field.editorType, { adminMode, onIssueKeyClick: handleIssueKeyClick })}
            {field.hasEditMeta && <span className="screen-field-edit-icon">&#9998;</span>}
          </div>
          {fieldError && <div className="screen-field-error">{fieldError}</div>}
        </>
      );
    }

    // Editing mode
    if (isEditing) {
      if (field.editorType === 'select' && field.allowedValues) {
        // Type to find the option: a select with a long list (a resource,
        // a supplier) is a search box, not a list to scroll. It opens
        // straight away — the click on the field was the click to pick.
        return (
          <div className="screen-field-edit">
            <SearchableSelect
              options={field.allowedValues}
              value={editValue?.id || ''}
              onChange={(id) => {
                const selected = field.allowedValues.find(v => String(v.id) === String(id));
                handleSave(field.key, selected || null, field.editorType);
              }}
              onDismiss={() => setEditingFieldId(null)}
              disabled={isSaving}
              defaultOpen
            />
          </div>
        );
      }

      if (field.editorType === 'cascading-select' && field.allowedValues) {
        return (
          <CascadingSelectEditor
            options={field.allowedValues}
            value={editValue && typeof editValue === 'object' ? editValue : null}
            onSave={(picked) => handleSave(field.key, picked, field.editorType)}
            onCancel={() => setEditingFieldId(null)}
            disabled={isSaving}
          />
        );
      }

      if (field.editorType === 'date') {
        // Stride's own calendar, not the browser's. A native <input type="date">
        // hands the popup to the browser, whose month arrows dismiss it before a
        // day can be picked — behaviour we can neither hook nor test. DateEditor's
        // month nav only moves local view state, so the calendar stays open until
        // the user actually picks a day (or Today/Clear). Escape still closes the
        // editor through the panel's own keydown handler, and this is the same
        // editor the Structure grid already uses via FieldEditor.
        // The calendar floats over the panel rather than sitting in the column's
        // flow: an inline one pushed every field below it down while picking,
        // and grew a row taller on a six-week month. Same card as the grid's
        // popover, and a ✕ to leave without picking (Escape still works too).
        return (
          <div className="screen-field-edit screen-field-edit-date">
            {/* The field keeps showing its value while the calendar is open
                under it — the calendar no longer covers the very field it edits. */}
            <div className="screen-field-value screen-field-value--editing">
              {editValue ? (formatIfCanonicalDate(editValue) ?? editValue) : <em>None</em>}
            </div>
            <div className="screen-field-calendar" ref={dateCalendarRef}>
              <button
                type="button"
                className="screen-field-calendar-close"
                onClick={() => setEditingFieldId(null)}
                title="Close"
                aria-label="Close date picker"
              >
                &times;
              </button>
              <DateEditor
                value={editValue || ''}
                disabled={isSaving}
                onAutoSave={(next) => handleSave(field.key, next, field.editorType)}
              />
            </div>
          </div>
        );
      }

      if (field.editorType === 'number') {
        return (
          <div className="screen-field-edit">
            <input type="number" className="screen-field-input" value={editValue ?? ''} onChange={(e) => setEditValue(e.target.value)} onBlur={() => handleSave(field.key, editValue, field.editorType)} onKeyDown={(e) => handleEditKeyDown(e, field.key, field.editorType)} disabled={isSaving} autoFocus />
          </div>
        );
      }

      if (field.editorType === 'time-display') {
        // Convert seconds to display string for editing
        const displayVal = typeof editValue === 'number'
          ? (() => { const h = Math.floor(editValue / 3600); const m = Math.floor((editValue % 3600) / 60); return h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : m > 0 ? `${m}m` : '0m'; })()
          : (editValue || '');
        return (
          <div className="screen-field-edit">
            <input type="text" className="screen-field-input" value={displayVal} onChange={(e) => setEditValue(e.target.value)} onBlur={() => handleSave(field.key, editValue, field.editorType)} onKeyDown={(e) => handleEditKeyDown(e, field.key, field.editorType)} disabled={isSaving} autoFocus placeholder="e.g. 1w 2d 4h 30m" />
          </div>
        );
      }

      // Default: text input
      return (
        <div className="screen-field-edit">
          <input type="text" className="screen-field-input" value={editValue ?? ''} onChange={(e) => setEditValue(e.target.value)} onBlur={() => handleSave(field.key, editValue, field.editorType)} onKeyDown={(e) => handleEditKeyDown(e, field.key, field.editorType)} disabled={isSaving} autoFocus />
        </div>
      );
    }

    // Display mode
    return (
      <>
        <div
          className={`screen-field-value ${isEditable ? 'editable' : ''} ${hasSuccess ? 'save-flash' : ''}`}
          onClick={isEditable ? () => handleFieldClick(field) : undefined}
        >
          {formatDisplayValue(field.key, value, field.editorType, { adminMode, onIssueKeyClick: handleIssueKeyClick })}
          {isEditable && <span className="screen-field-edit-icon">&#9998;</span>}
        </div>
        {fieldError && <div className="screen-field-error">{fieldError}</div>}
      </>
    );
  };

  // Check if ANY tab has issue layout sections (content vs primary/secondary)
  const hasLayoutSections = layoutHasSections(tabs);

  // The tabs offered (screenSections.js): with a layout, a tab whose fields
  // all live in the sidebar is not offered — Jira does not show it either.
  // The open tab falls to the first offered one when it is not offered.
  const shownTabs = bodyTabIndexes(tabs);
  const shownTabIndex = shownTabs.includes(activeTab) ? activeTab : (shownTabs.length ? shownTabs[0] : activeTab);
  const currentTab = tabs[shownTabIndex];

  // Content fields come from the current tab only
  // A Line items table needs the width: it joins the content column even when
  // the layout put it in the sidebar.
  const contentFields = hasLayoutSections
    ? currentTab?.fields.filter(isContentField) || []
    : null;

  // Sidebar fields are collected from ALL tabs so the sidebar persists across
  // tab switches: all of them in Details (primary, then secondary).
  const sidebar = hasLayoutSections ? sidebarFields(tabs) : null;
  const contextFields = sidebar ? sidebar.details : null;

  // Fallback: split into full-width and grid (for screen layout / editmeta mode)
  const fullWidthKeys = new Set(['description']);
  // Line items tables are full width too; with Description they head the
  // tab in screen order (a table placed after Description sits under it).
  const isTable = (f) => f.editorType === 'lineitems' || f.editorType === 'whiteboard' || f.editorType === 'document';
  const isFullWidth = (f) => fullWidthKeys.has(f.key) || isTable(f);
  const topFields = !hasLayoutSections ? (currentTab?.fields.filter(isFullWidth) || []) : [];
  const gridFields = !hasLayoutSections ? (currentTab?.fields.filter(f => !isFullWidth(f)) || []) : [];

  const renderField = (field, className = 'screen-field') => {
    const marks = (
      <>
        {field.required && <span className="screen-required">*</span>}
        {field.fieldLock && (
          <span className="screen-field-lock" title={field.fieldLock} aria-label={field.fieldLock}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </span>
        )}
      </>
    );
    // a fork's own view of the field may carry its own label and fold away (FieldView/fieldViews.js)
    const viewOpts = fieldViewOptions(field);
    if (viewOpts) {
      return (
        <FieldViewSection key={field.key} className={className} fieldKey={field.key} label={viewOpts.label || field.name} collapsible={viewOpts.collapsible} marks={marks}>
          {renderFieldValue(field)}
        </FieldViewSection>
      );
    }
    return (
      <div key={field.key} className={className}>
        <div className="screen-field-label">
          {field.name}
          {marks}
          {field.editorType === 'rich-text' && editingFieldId !== field.key
            && hasDraft(user?.accountId, issueKey, `field:${field.key}`)
            && <span className="screen-unsaved-mark">&bull; Unsaved changes</span>}
        </div>
        {renderFieldValue(field)}
      </div>
    );
  };

  // Attachments from issue data, with fallback to individually-fetched attachments
  const issueAttachments = mergedIssueData?.fields?.attachment;
  const attachments = (issueAttachments?.length > 0 ? issueAttachments : fetchedAttachments) || [];

  // Build authenticated URL for attachment content
  const getAuthAttachmentUrl = useCallback((attachmentId, { download = false } = {}) => {
    const url = api.getAttachmentContentUrl(attachmentId);
    const params = [];
    // download=1 asks the server for an attachment disposition — the
    // browser saves the file under its own name instead of previewing it.
    if (download) params.push('download=1');
    const token = localStorage.getItem('token');
    if (token) params.push(`token=${encodeURIComponent(token)}`);
    if (!params.length) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${params.join('&')}`;
  }, []);

  // Stable loaders for the card thumbnail and the preview's documents
  // (header auth, no token in the URL — see api-core).
  const loadAttachmentThumb = useCallback((att) => api.getAttachmentThumbBlob(att.id), []);
  const loadAttachmentBlob = useCallback((att) => api.getAttachmentBlob(att.id), []);

  // Attachments uploaded from inside a comment editor also belong on the
  // Attachments section — same handoff the wiki-field editors use.
  const uploadFromCommentEditor = useCallback(async (file) => {
    const result = await api.uploadAttachment(issueKey, file);
    const attachment = result[0];
    const existing = mergedIssueData?.fields?.attachment || fetchedAttachments || [];
    const updated = [...existing, attachment];
    setFetchedAttachments(updated);
    applyFieldUpdate(issueKey, { attachment: updated });
    return {
      ...attachment,
      content: api.getAttachmentContentUrl(attachment.id),
      mediaApiFileId: attachment.mediaApiFileId || null,
    };
  }, [issueKey, mergedIssueData?.fields?.attachment, fetchedAttachments, applyFieldUpdate]);

  // "3 minutes ago" / "2 days ago", with the absolute timestamp on hover —
  // how Jira stamps a comment. Falls back to the absolute date past a month,
  // where relative wording stops being useful.
  const relativeTime = (iso) => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const secs = Math.floor((Date.now() - then) / 1000);
    if (secs < 45) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return formatDate(iso, 'datetime');
  };

  // Own comments are always deletable (the server still gates on
  // DELETE_OWN_COMMENTS); other people's only when this user can delete on
  // this project. The server is the authority either way — this just avoids
  // offering a button that would 403.
  const canDeleteComment = (comment) =>
    canDelete || (!!user?.accountId && comment.author?.accountId === user.accountId);
  const canDeleteAttachment = (att) =>
    attachmentPerms.deleteAll
    || (attachmentPerms.deleteOwn && !!user?.accountId && att.author?.accountId === user.accountId);

  // Opening the composer FIXES the audience, so the label on the Save
  // button and the banner above it always agree with what will be sent.
  // The "+ Name" chips under the comment box: people involved with this
  // item, ranked server-side. Asked for only once a composer is open.
  const { data: mentionSuggestions } = useMentionSuggestions(issueKey, {
    enabled: !!issueKey && (showCommentEditor || !!replyingTo),
  });

  const openComposer = (internal) => {
    setAddCommentError(null);
    setCommentInternal(!!internal);
    setShowCommentEditor(true);
    // A comment typed before and not saved opens again — only for the
    // audience it was written to, so an internal note never comes back as
    // a reply to the customer.
    const draft = readDraft(user?.accountId, issueKey, 'comment');
    setNewCommentText(draft && !!draft.internal === !!internal ? draft.doc : { type: 'doc', content: [{ type: 'paragraph' }] });
  };

  const renderCommentEditor = ({ value, onChange, onSave, onCancel, saveLabel, autoFocus }) => (
    <div className="comment-add-editor">
      <RichTextEditor
        value={value}
        onChange={onChange}
        disabled={addingComment}
        onFileUpload={attachmentPerms.create ? uploadFromCommentEditor : undefined}
        autoFocus={autoFocus}
        mentionSuggestions={mentionSuggestions}
      />
      {addCommentError && <div className="comment-add-error">{addCommentError}</div>}
      <div className="comment-add-actions">
        <button className="comment-save-btn" onClick={onSave} disabled={addingComment}>
          {addingComment ? 'Saving...' : saveLabel}
        </button>
        <button className="comment-cancel-btn" onClick={onCancel} disabled={addingComment}>
          Cancel
        </button>
      </div>
    </div>
  );

  // One comment — used for thread roots and replies alike (`isReply` only
  // changes the chrome, never the behaviour).
  const renderComment = (comment, { isReply = false, rootId = null } = {}) => {
    // Same treatment as rich-text fields: resolve embedded media against the
    // attachment list so files show as named chips.
    const commentBody = adfToTipTap(comment.body);
    resolveImageRefs(commentBody, attachments, mediaMap);
    const authorName = comment.author?.displayName || 'Unknown';
    const edited = comment.updated && comment.created && comment.updated !== comment.created;

    const focused = focusCommentId != null && String(comment.id) === String(focusCommentId);
    return (
      <div id={`comment-${comment.id}`} className={`comment-item ${isReply ? 'comment-item--reply' : ''} ${focused ? 'comment-item--focus' : ''} ${comment.internal ? 'comment-item--internal' : ''}`}>
        <UserAvatar
          user={comment.author}
          size={isReply ? 24 : 32}
          className="comment-avatar"
          resolveImageUrl={api.getJiraImageUrl}
        />
        <div className="comment-main">
          <div className="comment-header">
            <span className="comment-author">{authorName}</span>
            <span className="comment-date" title={formatDate(comment.created, 'datetime')}>
              {relativeTime(comment.created)}
            </span>
            {edited && (
              <span className="comment-edited" title={`Edited ${formatDate(comment.updated, 'datetime')}`}>
                edited
              </span>
            )}
            {/* An internal note is SAID as well as shown. Colour alone would
                leave it unmarked for anyone who cannot see the tint, and
                "can the customer read this?" is not a question to answer by
                eye. */}
            {comment.internal && (
              <span className="comment-internal-tag" title="Only people working this request can see this">
                Internal note
              </span>
            )}
          </div>
          <div className="comment-body">
            {/* Key re-mounts when the attachment list arrives — TipTap
                only reads `value` on mount. */}
            <RichTextEditor key={`${comment.id}-${attachments.length}`} value={commentBody} readOnly />
          </div>
          <div className="comment-actions">
            <button
              type="button"
              className="comment-action-btn"
              onClick={() => {
                setAddCommentError(null);
                setReplyingTo(rootId || comment.id);
                // Opens on the thread's unsaved reply if there is one, else
                // with the author @mentioned, as Jira's Reply does.
                const draft = readDraft(user?.accountId, issueKey, `reply:${rootId || comment.id}`);
                setReplyText(draft ? draft.doc : replyDraft(comment));
              }}
            >
              Reply
            </button>
            {!isReply && replyingTo !== comment.id && hasDraft(user?.accountId, issueKey, `reply:${comment.id}`)
              && <span className="screen-unsaved-mark">&bull; Unsaved changes</span>}
            {canDeleteComment(comment) && (
              <button
                type="button"
                className="comment-action-btn comment-action-btn--danger"
                onClick={() => setDeletingComment(comment)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Activity section render functions
  const renderCommentsTab = () => {
    if (commentsLoading) {
      return (
        <div className="screen-panel-loading">
          <span className="screen-spinner"></span>
          Loading comments...
        </div>
      );
    }
    if (commentsError) {
      return <div className="screen-panel-error">{commentsError}</div>;
    }

    return (
      <div className="comments-content">
        {/* Add comment trigger / editor */}
        {!showCommentEditor ? (
          <div className="comment-add-trigger">
            <UserAvatar user={user} size={28} className="comment-avatar" resolveImageUrl={api.getJiraImageUrl} />
            {isServiceDesk ? (
              // The agent says WHO they are writing to before they write.
              // Asking afterwards is how an internal note gets sent to a
              // customer.
              <span className="comment-add-audience">
                <button type="button" className="comment-audience-btn"
                  onClick={() => openComposer(true)}>Add internal note</button>
                <span className="comment-audience-sep"> / </span>
                <button type="button" className="comment-audience-btn"
                  onClick={() => openComposer(false)}>Reply to customer</button>
              </span>
            ) : (
              <span className="comment-add-trigger-text" onClick={() => openComposer(false)}>Add a comment...</span>
            )}
            {hasDraft(user?.accountId, issueKey, 'comment') && <span className="screen-unsaved-mark">&bull; Unsaved changes</span>}
          </div>
        ) : (
          <div className={isServiceDesk && commentInternal ? 'comment-compose comment-compose--internal' : 'comment-compose'}>
            {isServiceDesk && (
              <div className="comment-audience-bar">
                {commentInternal
                  ? <span><strong>Internal note</strong> — only people working this request will see it.</span>
                  : <span><strong>Reply to customer</strong> — this appears on the portal.</span>}
                <button type="button" className="comment-audience-switch"
                  onClick={() => {
                    setCommentInternal(!commentInternal);
                    if (hasDraft(user?.accountId, issueKey, 'comment')) {
                      writeDraft(user?.accountId, issueKey, 'comment', { doc: newCommentText, internal: !commentInternal });
                    }
                  }}>
                  {commentInternal ? 'Reply to customer instead' : 'Make it an internal note'}
                </button>
              </div>
            )}
            {renderCommentEditor({
              value: newCommentText,
              onChange: (doc) => {
                setNewCommentText(doc);
                writeDraft(user?.accountId, issueKey, 'comment', { doc, internal: commentInternal });
              },
              onSave: handleAddComment,
              onCancel: () => {
                setShowCommentEditor(false); setNewCommentText(null); setAddCommentError(null);
                clearDraft(user?.accountId, issueKey, 'comment');
              },
              saveLabel: isServiceDesk && commentInternal ? 'Add internal note' : 'Save',
              autoFocus: true,
            })}
          </div>
        )}

        {commentCount > 1 && (
          <div className="comment-sort-bar">
            <span className="comment-sort-count">
              {commentCount} comment{commentCount === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="comment-sort-btn"
              onClick={() => setCommentSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))}
              title={commentSortOrder === 'desc' ? 'Showing newest first' : 'Showing oldest first'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {commentSortOrder === 'desc' ? (
                  <><path d="M12 5v14" /><path d="M19 12l-7 7-7-7" /></>
                ) : (
                  <><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></>
                )}
              </svg>
              {commentSortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </button>
          </div>
        )}

        {/* Threads: a root comment plus everything replying to it. */}
        {commentThreads.length > 0 ? (
          <div className="comment-list">
            {commentThreads.map(({ root, replies }) => (
              <div key={root.id} className="comment-thread">
                {renderComment(root)}
                {replies.length > 0 && (
                  <div className="comment-replies">
                    {replies.map(reply => (
                      <div key={reply.id}>
                        {renderComment(reply, { isReply: true, rootId: root.id })}
                      </div>
                    ))}
                  </div>
                )}
                {replyingTo === root.id && (
                  <div className="comment-replies comment-reply-compose">
                    {renderCommentEditor({
                      value: replyText,
                      onChange: (doc) => {
                        setReplyText(doc);
                        writeDraft(user?.accountId, issueKey, `reply:${root.id}`, { doc });
                      },
                      onSave: () => handleAddReply(root.id),
                      onCancel: () => {
                        setReplyingTo(null); setReplyText(null); setAddCommentError(null);
                        clearDraft(user?.accountId, issueKey, `reply:${root.id}`);
                      },
                      saveLabel: 'Reply',
                      autoFocus: true,
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : comments !== null ? (
          <div className="screen-panel-empty">No comments yet</div>
        ) : null}
      </div>
    );
  };

  const renderHistoryTab = () => {
    if (changelogLoading) {
      return (
        <div className="screen-panel-loading">
          <span className="screen-spinner"></span>
          Loading history...
        </div>
      );
    }
    if (changelogError) {
      return <div className="screen-panel-error">{changelogError}</div>;
    }
    // Build created entry from issue data
    const creator = mergedIssueData?.fields?.creator;
    const createdDate = mergedIssueData?.fields?.created;
    const createdEntry = createdDate ? {
      id: 'created',
      author: creator,
      created: createdDate,
      items: [{ field: 'Created', toString: issueKey }],
      isCreated: true,
    } : null;

    const allEntries = [...(changelog || []), ...(createdEntry ? [createdEntry] : [])]
      .sort((a, b) => {
        const dateA = a.created ? new Date(a.created).getTime() : 0;
        const dateB = b.created ? new Date(b.created).getTime() : 0;
        return historySortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });

    if (allEntries.length === 0) {
      return <div className="screen-panel-empty">No history available</div>;
    }

    return (
      <div className="history-list">
        <div className="history-sort-bar">
          <span className="history-sort-count">{allEntries.length} change{allEntries.length !== 1 ? 's' : ''}</span>
          <button
            className="history-sort-btn"
            onClick={() => setHistorySortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            title={historySortOrder === 'desc' ? 'Showing newest first' : 'Showing oldest first'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {historySortOrder === 'desc' ? (
                <><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></>
              ) : (
                <><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></>
              )}
            </svg>
            {historySortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          </button>
        </div>
        {allEntries.map(entry => (
          <div key={entry.id} className={`history-entry${entry.isCreated ? ' history-created' : ''}`}>
            <div className="history-header">
              {/* Same colour-coded fallback as comments — a cached avatar when
                  there is one, otherwise initials on a per-person colour. */}
              <UserAvatar
                user={entry.author}
                size={24}
                className="history-avatar"
                resolveImageUrl={api.getJiraImageUrl}
              />
              <span className="history-author">{entry.author?.displayName || 'Unknown'}</span>
              <span className="history-date">
                {formatDate(entry.created, 'datetime')}
              </span>
            </div>
            <div className="history-items">
              {(entry.items || []).map((item, idx) => (
                <div key={idx} className="history-change">
                  <span className="history-field">{item.field}</span>
                  {item.fromString && (
                    <span className="history-old">{item.fromString}</span>
                  )}
                  {(item.fromString || item.toString) && (
                    <span className="history-arrow">&rarr;</span>
                  )}
                  {item.toString && (
                    <span className="history-new">{item.toString}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAttachmentsSection = () => {
    return (
      <div className="attachments-content">
        {/* Upload button — only for people the scheme grants CREATE_ATTACHMENTS */}
        {attachmentPerms.create && (
          <>
            <button
              className="attachment-upload-btn"
              onClick={() => attachmentInputRef.current?.click()}
              disabled={uploadingAttachment}
            >
              {uploadingAttachment ? (
                <><span className="screen-spinner"></span> Uploading...</>
              ) : (
                <>+ Upload file</>
              )}
            </button>
            <input
              ref={attachmentInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.length) handleFilesUpload(e.target.files); }}
            />
          </>
        )}

        {/* Attachment grid */}
        {attachments.length > 0 ? (
          <div className="attachment-grid">
            {attachments.map((att, attIdx) => (
              <div key={att.id} className="attachment-card">
                <a
                  className="attachment-link"
                  href={getAuthAttachmentUrl(att.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={att.filename}
                  onClick={(e) => handleIssueLinkActivation(e, () => setPreviewIndex(attIdx))}
                >
                  <div className="attachment-thumb">
                    {/* The server-made thumbnail (image or page 1 of a PDF);
                        a file without one draws what it always did. */}
                    <AttachmentThumb
                      attachment={att}
                      load={loadAttachmentThumb}
                      fallback={isImageFile(att.filename)
                        ? <img src={getAuthAttachmentUrl(att.id)} alt={att.filename} loading="lazy" />
                        : getFileIcon(att.filename, 44)}
                    />
                  </div>
                  <div className="attachment-info">
                    <div className="attachment-name">{att.filename}</div>
                    <div className="attachment-meta">
                      {formatFileSize(att.size)}
                      {/* Date AND time, as Jira shows an upload. */}
                      {att.created && <> &middot; {formatDate(att.created, 'datetime')}</>}
                    </div>
                  </div>
                </a>
                <a
                  className="attachment-download-btn"
                  href={getAuthAttachmentUrl(att.id, { download: true })}
                  download={att.filename}
                  title={`Download ${att.filename}`}
                  aria-label={`Download ${att.filename}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 3v12" /><polyline points="6 11 12 17 18 11" /><path d="M4 21h16" />
                  </svg>
                </a>
                {canDeleteAttachment(att) && (
                  <button
                    className="attachment-delete-btn"
                    onClick={() => handleDeleteAttachment(att.id)}
                    title="Delete attachment"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="screen-panel-empty">No attachments</div>
        )}

        {previewIndex !== null && attachments[previewIndex] && (
          <AttachmentPreview
            attachments={attachments}
            index={previewIndex}
            onClose={() => setPreviewIndex(null)}
            onNavigate={setPreviewIndex}
            contentUrl={(a) => getAuthAttachmentUrl(a.id)}
            downloadUrl={(a) => getAuthAttachmentUrl(a.id, { download: true })}
            loadBlob={loadAttachmentBlob}
            renderFileIcon={getFileIcon}
            formatSize={formatFileSize}
            formatWhen={(iso) => formatDate(iso, 'datetime')}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className="issue-screen-panel"
      ref={panelRef}
      onDragEnter={handlePanelDragEnter}
      onDragLeave={handlePanelDragLeave}
      onDragOver={handlePanelDragOver}
      onDrop={handlePanelDrop}
    >
      {/* Header and tabs travel together and stay pinned while the panel is
          scrolled — see .screen-panel-sticky. An open work item is routinely
          taller than the viewport, and once the title scrolled away you were
          reading an anonymous form: no key, no status, no way back to the
          tabs without scrolling to the top. The pin releases at the foot of
          the panel, because a sticky child cannot outlive its parent.

          The wrapped block is deliberately NOT re-indented: shifting 200
          lines of header would bury this in a whitespace diff. */}
      <div className="screen-panel-sticky">
      {/* Header */}
      <div className="screen-panel-header">
        <div className="screen-panel-header-meta">
          {/* The parent (its parent FIELD) — "PARENT / KEY", as Jira draws
              it. A parent that is a link is listed with the links instead. */}
          {parentInfo.jiraParent?.key && (() => {
            const p = parentWithDetails(parentInfo.effectiveParent);
            return (
              <span className="screen-panel-parent-crumb">
                {p.fields?.issuetype?.iconUrl && (
                  <img src={api.getJiraImageUrl(p.fields.issuetype.iconUrl)} alt="" title={p.fields.issuetype.name || ''} width="14" height="14" />
                )}
                <a href={issueResultsHref(p.key)} title={p.fields?.summary || ''} onClick={(e) => handleIssueLinkActivation(e, handleIssueKeyClick, p.key)}>{p.key}</a>
                {!!creationTree && (
                  <button type="button" className="screen-panel-parent-crumb-edit" title="Change parent" onClick={() => setEditingParent(true)}>&#9998;</button>
                )}
                <span className="screen-panel-parent-crumb-sep">/</span>
              </span>
            );
          })()}
          {mergedIssueData?.fields?.issuetype?.iconUrl && (
            <img
              className="screen-panel-issuetype-icon"
              src={api.getJiraImageUrl(mergedIssueData.fields.issuetype.iconUrl)}
              alt={mergedIssueData.fields.issuetype.name || ''}
              title={mergedIssueData.fields.issuetype.name || ''}
              width="16"
              height="16"
            />
          )}
          {/* The key, and "Copy link" beside it (Jira's hover link icon). */}
          <WorkItemKey
            issueKey={issueKey}
            href={issueResultsHref(issueKey)}
            onKeyClick={(e) => handleIssueLinkActivation(e, handleIssueKeyClick, issueKey)}
          />
          {/* "+ Create" — a child of this item, as the Architectural
              Hierarchy allows. Drawn only when the project has a hierarchy;
              inert (with the reason) when the hierarchy gives this type no
              children. Sits between the key and the status. */}
          {creationTree && (
            <div className="screen-create-wrapper" ref={createMenuRef}>
              <button
                type="button"
                className={`screen-create-btn${createMenuOpen ? ' open' : ''}`}
                onClick={() => setCreateMenuOpen(prev => !prev)}
                disabled={hierarchyChildEdges.length === 0}
                title={hierarchyChildEdges.length === 0
                  ? `The Architectural Hierarchy defines no child types for ${panelTypeName || 'this work type'}`
                  : 'Create'}
                aria-label="Create"
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
              </button>
              {createMenuOpen && (
                <div className="screen-status-dropdown screen-create-menu" role="menu">
                  <div className="screen-create-menu-title">Create under {issueKey}</div>
                  {hierarchyCreateOptions === null ? (
                    <div className="screen-status-dropdown-loading">
                      <span className="screen-spinner"></span> Loading...
                    </div>
                  ) : hierarchyCreateOptions.length === 0 ? (
                    <div className="screen-create-menu-empty">
                      None of the child types the hierarchy allows for {panelTypeName} exist in this project.
                    </div>
                  ) : (
                    hierarchyCreateOptions.map(opt => (
                      <button
                        key={opt.issueTypeId}
                        type="button"
                        role="menuitem"
                        className="screen-status-option screen-create-option"
                        onClick={() => handleHierarchyCreatePick(opt)}
                      >
                        {opt.iconUrl ? (
                          <img className="screen-create-option-icon" src={api.getJiraImageUrl(opt.iconUrl)} alt="" width="16" height="16" />
                        ) : (
                          <span className="screen-create-option-icon screen-create-option-icon-blank" />
                        )}
                        <span className="screen-status-option-label">{opt.issueTypeName}</span>
                        <span className="screen-status-option-transition">
                          {opt.edge?.relationshipType === 'subtask' || opt.edge?.relationshipType === 'child' ? 'sub-task' : (opt.edge?.linkTypeName || LINK_TYPE_NAME)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          {/* Status transition button */}
          {(() => {
            const status = mergedIssueData?.fields?.status;
            if (!status) return null;
            const colorName = status.statusCategory?.colorName;
            const categoryClass = colorName === 'green' ? 'done'
              : colorName === 'yellow' ? 'in-progress'
              : colorName === 'blue-gray' ? 'todo' : 'todo';
            return (
              <div className="screen-status-wrapper" ref={statusDropdownRef}>
                <button
                  className={`screen-status-btn status-cat-${categoryClass} ${transitionLoading ? 'transitioning' : ''}`}
                  onClick={() => setStatusDropdownOpen(prev => !prev)}
                  disabled={transitionLoading}
                >
                  {transitionLoading ? (
                    <>
                      <span className="screen-spinner screen-spinner-sm" />
                      <span className="screen-status-name">{transitioningName}...</span>
                    </>
                  ) : (
                    <>
                      <span className="screen-status-name">{status.name?.toUpperCase()}</span>
                      <span className="screen-status-chevron">{statusDropdownOpen ? '▴' : '▾'}</span>
                    </>
                  )}
                </button>
                {statusDropdownOpen && (
                  <div className="screen-status-dropdown">
                    {transitions.length === 0 ? (
                      <div className="screen-status-dropdown-loading">
                        <span className="screen-spinner"></span> Loading...
                      </div>
                    ) : (
                      transitions
                        .filter(t => (t.to?.name || '').toLowerCase() !== (status.name || '').toLowerCase())
                        .map(t => {
                        const tColor = t.to?.statusCategory?.colorName;
                        const tClass = tColor === 'green' ? 'done'
                          : tColor === 'yellow' ? 'in-progress' : 'todo';
                        const targetName = t.to?.name || t.name;
                        // Show the transition name on the right (e.g. "FORWARD",
                        // "back", "Opportunity") when it differs from the target
                        // status name — matches how the Structure folder's inline
                        // status editor renders the same dropdown.
                        const showTransitionName = t.name && t.name !== targetName;
                        return (
                          <button
                            key={t.id}
                            className="screen-status-option"
                            onClick={() => {
                              setStatusDropdownOpen(false);
                              if (t.hasScreen) setTransitionScreenFor(t);
                              else handleTransition(t.id, targetName);
                            }}
                            disabled={transitionLoading}
                          >
                            <span className={`screen-status-dot status-dot-${tClass}`} />
                            <span className="screen-status-option-label">{targetName}</span>
                            {showTransitionName && (
                              <span className="screen-status-option-transition">{t.name}</span>
                            )}
                          </button>
                        );
                      })
                    )}
                    <div className="screen-status-workflow-divider" />
                    <button
                      className="screen-status-workflow-btn"
                      onClick={(e) => { e.stopPropagation(); setStatusDropdownOpen(false); handleViewWorkflow(); }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="5" cy="12" r="2"/><circle cx="19" cy="6" r="2"/><circle cx="19" cy="18" r="2"/>
                        <path d="M7 12h4l6-6"/><path d="M11 12l6 6"/>
                      </svg>
                      View Workflow
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
          {/* The work item's project, for reference, in the space beside the
              status (a client, 2026-10-26: "next to the status … the project
              name present please"). */}
          {(mergedIssueData?.fields?.project?.name || mergedIssueData?.fields?.project?.key) && (
            <span className="screen-header-project" title={`Project: ${mergedIssueData.fields.project.name || ''}${mergedIssueData.fields.project.key ? ` (${mergedIssueData.fields.project.key})` : ''}`}>
              <span className="screen-header-project-label">Project</span>
              <span className="screen-header-project-name">{mergedIssueData.fields.project.name || mergedIssueData.fields.project.key}</span>
            </span>
          )}
          {transitionError && (
            <div className="screen-transition-error" onClick={() => { setTransitionError(null); setTransitionReasons([]); }} title="Click to dismiss">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <div className="screen-transition-error-body">
                <div className="screen-transition-error-msg">{transitionError}</div>
                {transitionReasons.length > 0 && (
                  <ul className="screen-transition-reasons">
                    {transitionReasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}
          <div className="screen-panel-header-actions">
            {/* SEE WHAT THE CUSTOMER SEES. Drawn only for a work item the
                portal actually raised — an agent-created item on a desk has
                no customer view to open. Opens in a new tab on purpose: the
                portal is a different chrome entirely, and losing the work
                item to get to it would be a poor trade. */}
            {portalRequest && (
              <a
                className="screen-panel-portal"
                href={`/portal/requests/${encodeURIComponent(portalRequest.key)}`}
                target="_blank"
                rel="noreferrer"
                title={portalRequest.requestTypeName
                  ? `Open the customer's view of this request (${portalRequest.requestTypeName})`
                  : "Open the customer's view of this request"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                <span>Portal</span>
              </a>
            )}
            {/* Security level — Jira's header lock. Drawn when the project
                has a security scheme (or the item carries a level from one
                it used to have): red when a level is set, muted when the
                item is unrestricted. The tooltip names the level; the click
                opens the scheme's levels for anyone holding
                SET_ISSUE_SECURITY, and is inert otherwise. */}
            {(() => {
              const level = mergedIssueData?.fields?.security || null;
              const levels = securityInfo?.levels || [];
              if (!level && levels.length === 0) return null;
              // A level open to every signed-in user (a scheme's "OPEN") restricts
              // nobody: its lock is drawn open, not red, so a closed lock always
              // means someone is kept out.
              const openLevel = !!level && levels.some(l => l.id === String(level.id) && l.openToAll);
              const locked = !!level && !openLevel;
              const label = !level ? 'No security level — visible to everyone who can see the project'
                : openLevel ? `Security level: ${level.name || level.id} — open to everyone who can see the project`
                : `Security level: ${level.name || level.id}`;
              const interactive = canSetSecurity && levels.length > 0 && !securitySaving;
              return (
                <div className="screen-panel-security-wrap" ref={securityMenuRef}>
                  <button
                    type="button"
                    className={`screen-panel-security ${locked ? 'is-locked' : 'is-open'} ${interactive ? 'is-interactive' : ''}`}
                    onClick={interactive ? () => setSecurityMenuOpen(o => !o) : undefined}
                    title={interactive ? `${label} — click to change` : label}
                    aria-label={label}
                    aria-haspopup={interactive ? 'menu' : undefined}
                    aria-expanded={interactive ? securityMenuOpen : undefined}
                    disabled={securitySaving}
                  >
                    {locked ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
                      </svg>
                    )}
                  </button>
                  {securityMenuOpen && (
                    <div className="screen-status-dropdown screen-panel-security-menu" role="menu">
                      <div className="screen-panel-security-menu-title">
                        Security level{securityInfo?.scheme?.name ? ` · ${securityInfo.scheme.name}` : ''}
                      </div>
                      <button type="button" className={`screen-status-option ${!level ? 'is-current' : ''}`} role="menuitem" onClick={() => handleSecurityChange(null)}>
                        <span className="screen-status-option-label">No restriction</span>
                        {!level && <span className="screen-status-option-transition">current</span>}
                      </button>
                      {levels.map(l => (
                        <button key={l.id} type="button" className={`screen-status-option ${level?.id === l.id ? 'is-current' : ''}`} role="menuitem"
                          title={l.description || undefined}
                          onClick={() => handleSecurityChange(l)}>
                          <span className="screen-status-option-label">{l.name}</span>
                          {level?.id === l.id && <span className="screen-status-option-transition">current</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Configure layout — admin only. Lives up here beside Delete
                rather than at the foot of the Details sidebar, where it was
                a long scroll away from the fields it configures. */}
            {adminMode && configureUrl && (
              <button
                type="button"
                className="screen-panel-configure"
                onClick={() => openTab(configureUrl, 'Screen Layout')}
                title="Configure work item layout"
                aria-label="Configure work item layout"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            )}
            {canDelete && !deleteConfirm && (
              <button
                className="screen-panel-delete"
                onClick={() => setDeleteConfirm(true)}
                disabled={deleting}
                title="Delete work item"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            )}
            {deleteConfirm && (
              <div className="screen-panel-delete-confirm">
                <span>Delete?</span>
                <button className="screen-panel-delete-yes" onClick={handleDeleteIssue} disabled={deleting}>
                  {deleting ? '...' : 'Yes'}
                </button>
                <button className="screen-panel-delete-no" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                  No
                </button>
              </div>
            )}
            <ExportReportButton
              issueKey={issueKey}
              issueTypeId={mergedIssueData?.fields?.issuetype?.id}
            />
            <button
              className="screen-panel-refresh"
              onClick={() => loadFields(true)}
              disabled={loading}
              title="Refresh fields from Jira"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
            {onClose && (
            <button className="screen-panel-close" onClick={onClose} title="Close panel">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            )}
          </div>
          {saveErrors.security && (
            <div className="screen-transition-error" onClick={() => setSaveErrors(prev => { const { security, ...rest } = prev; return rest; })} title="Click to dismiss">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {saveErrors.security}
            </div>
          )}
          {saveErrors.__delete && (
            <div className="screen-transition-error" onClick={() => setSaveErrors(prev => { const { __delete, ...rest } = prev; return rest; })} title="Click to dismiss">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {saveErrors.__delete}
            </div>
          )}
        </div>
        {editingSummary ? (
          <input
            ref={summaryInputRef}
            type="text"
            className="screen-panel-summary-input"
            value={summaryDraft}
            onChange={(e) => setSummaryDraft(e.target.value)}
            onKeyDown={handleSummaryKeyDown}
            onBlur={commitSummaryEdit}
            aria-label="Work item summary"
            spellCheck={false}
          />
        ) : (
          <h2
            className={`screen-panel-summary ${summaryEditable ? 'is-editable' : ''} ${saving.summary ? 'is-saving' : ''}`}
            onDoubleClick={beginSummaryEdit}
            title={summaryEditable ? 'Double-click to edit the summary' : undefined}
          >
            {mergedIssueData?.fields?.summary || 'Edit Fields'}
          </h2>
        )}
        {saveErrors.summary && (
          <div className="screen-transition-error" onClick={() => setSaveErrors(prev => { const { summary, ...rest } = prev; return rest; })} title="Click to dismiss">
            {saveErrors.summary}
          </div>
        )}
      </div>

      {/* Tab bar — only show when there are multiple tabs */}
      {shownTabs.length > 1 && (
        <div className="screen-tab-bar">
          {shownTabs.map((i) => (
            <button
              key={tabs[i].id}
              className={`screen-tab ${i === shownTabIndex ? 'active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {tabs[i].name}
            </button>
          ))}
        </div>
      )}
      </div>{/* /.screen-panel-sticky */}

      {/* Body */}
      <div className="screen-panel-body">
        {/* Link issue form */}
        {showLinkIssue && (
          <div className="screen-link-form">
            <div className="screen-link-form-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span>Link Work Item</span>
              <button className="screen-link-form-close" onClick={() => { setShowLinkIssue(false); setLinkError(null); }}>&times;</button>
            </div>
            <div className="screen-link-form-input-row">
              <IssuePickerInput
                value={linkForm.targetIssueKey}
                onChange={(e) => setLinkForm(prev => ({ ...prev, targetIssueKey: e.target.value }))}
                onSelect={(key) => setLinkForm(prev => ({ ...prev, targetIssueKey: key }))}
                currentIssueKey={issueKey}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleLinkIssue(); } }}
                disabled={linkSubmitting}
                placeholder="Search or enter work item key..."
                autoFocus
              />
              <button className="screen-link-form-submit" onClick={handleLinkIssue} disabled={linkSubmitting || !linkForm.targetIssueKey.trim()}>
                {linkSubmitting ? <span className="screen-spinner-sm"></span> : 'Link'}
              </button>
            </div>
            {linkError && <div className="screen-create-error">{linkError}</div>}
            {linkSuccess && <div className="screen-create-success">Work item linked successfully</div>}
          </div>
        )}

        {loading && (
          <div className="screen-panel-loading">
            <span className="screen-spinner"></span>
            Loading fields...
          </div>
        )}

        {error && (
          <div className="screen-panel-error">{error}</div>
        )}

        {!loading && !error && tabs.length === 0 && (
          <div className="screen-panel-empty">No editable fields available</div>
        )}

        {/* Two-column layout: content (left) + context sidebar (right) */}
        {/* A layout with no Details fields draws no Details card (and no
            divider): the main column takes the whole width (showDetailsPanel). */}
        {!loading && !error && currentTab && hasLayoutSections && (
          <div className={`screen-layout-columns${showDetailsPanel(sidebar) ? '' : ' screen-layout-columns--no-details'}`} ref={columnsRef}>
            <div className="screen-layout-main">
              {contentFields.length > 0 ? (
                contentFields.map(field => renderField(field, 'screen-field full-width'))
              ) : (
                <div className="screen-panel-empty">No description fields configured</div>
              )}
            </div>
            {showDetailsPanel(sidebar) && (
              <>
                <div className="screen-layout-divider" onMouseDown={handleDividerMouseDown}>
                  <div className="screen-layout-divider-grip" />
                </div>
                <div className="screen-layout-sidebar" style={{ width: sidebarWidthPct + '%' }}>
                  <div className="screen-sidebar-header">Details</div>
                  {contextFields.map(field => renderField(field))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Fallback: single-column layout (screen layout / editmeta) */}
        {!loading && !error && currentTab && !hasLayoutSections && (
          <>
            {topFields.map(field => renderField(field, 'screen-field full-width'))}
            <div className="screen-fields-grid">
              {gridFields.map(field => renderField(field))}
            </div>
          </>
        )}

        {/* Dedicated sections for complex fields */}
        {!loading && !error && (() => {
          const subtasks = mergedIssueData?.fields?.subtasks || [];
          const issueLinks = mergedIssueData?.fields?.issuelinks || [];
          const timeTracking = mergedIssueData?.fields?.timetracking;
          const watchCount = mergedIssueData?.fields?.watches?.watchCount || 0;
          const voteCount = mergedIssueData?.fields?.votes?.votes || 0;

          // The effective parent: Jira parent field OR linked parent (see parentInfo).
          // The Parent group shows the parent FIELD only; a parent that is a
          // link is listed with the links, under its phrase ("Link to
          // Parent"), so what is a link always reads as one.
          const { effectiveParent, hasParent } = parentInfo;
          const canSetParent = !!creationTree;

          // Determine which link types represent tree children of this issue type
          const currentTypeName = issueData?.fields?.issuetype?.name;
          const treeChildTypes = creationTree ? getAllowedChildTypes(currentTypeName, creationTree, LINK_TYPE_NAME) : [];
          const treeParentTypes = creationTree ? getAllowedParentRelationships(currentTypeName, creationTree, LINK_TYPE_NAME) : [];
          // Unified relationship options for the "+ Add link" picker — both
          // directions, role-tagged so the submit handler knows which side
          // the candidate plays.
          const relationshipOptions = [
            ...treeChildTypes.map(edge => ({
              role: 'child',
              candidateTypeName: edge.issueTypeName,
              edge,
            })),
            ...treeParentTypes.map(edge => ({
              role: 'parent',
              candidateTypeName: edge.parentTypeName,
              edge,
            })),
          ];
          // The "+" picker's first step: one choice per RELATIONSHIP (link
          // type + side, or sub-task / child), each listing the work types it
          // allows — then the work item. The tree has one edge per type, so
          // its options are folded by what the link will actually be.
          const relationshipGroups = groupRelationshipOptions(relationshipOptions, linkTypes, LINK_TYPE_NAME);

          // Every issue link is listed under the phrase THIS work item shows
          // for it ("Link to Items on Debtor"), the way Jira lists them — a
          // hierarchy link included (the linked parent too), so it reads as
          // what it is and can be unlinked from here. Parent and Children
          // are the native ones: the parent field, sub-tasks, an epic's.
          const otherLinks = []; // link groups, keyed by phrase
          if (issueLinks.length > 0) {
            const otherGroups = {};
            for (const link of issueLinks) {
              let label, issue;
              if (link.inwardIssue) {
                label = link.type?.inward || link.type?.name || 'linked';
                issue = link.inwardIssue;
              } else if (link.outwardIssue) {
                label = link.type?.outward || link.type?.name || 'linked';
                issue = link.outwardIssue;
              } else {
                continue;
              }
              if (!otherGroups[label]) otherGroups[label] = { label, issues: [] };
              otherGroups[label].issues.push({ linkId: link.id, issue });
            }
            otherLinks.push(...Object.values(otherGroups));
          }

          // Native children: subtasks + fetched children (epics), deduplicated
          const seenChildren = new Set();
          const allChildren = [];
          for (const sub of subtasks) {
            if (sub.key && !seenChildren.has(sub.key)) { seenChildren.add(sub.key); allChildren.push(sub); }
          }
          for (const child of fetchedChildren) {
            if (child.key && !seenChildren.has(child.key)) { seenChildren.add(child.key); allChildren.push(child); }
          }

          const ttSpent = timeTracking?.timeSpentSeconds || 0;
          const ttOriginal = timeTracking?.originalEstimateSeconds || 0;
          const ttPct = ttOriginal > 0 ? Math.min(100, Math.round((ttSpent / ttOriginal) * 100)) : 0;

          return (
            <>
              {/* Panel-level insight picker */}
              {insights.length > 0 && (
                <div className="panel-insight-bar" ref={panelInsightRef}>
                  <button
                    className={`panel-insight-btn${panelInsightId !== null ? ' active' : ''}`}
                    onClick={() => setShowPanelInsightPicker(v => !v)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                    </svg>
                    <span>{panelInsightId !== null ? (insights.find(i => i.id === panelInsightId)?.name || 'Insight') : 'Folder Default'}</span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                  {showPanelInsightPicker && (
                    <div className="panel-insight-dropdown">
                      <div
                        className={`panel-insight-option${panelInsightId === null ? ' active' : ''}`}
                        onClick={() => { setPanelInsightId(null); setShowPanelInsightPicker(false); }}
                      >
                        {panelInsightId === null && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                        Folder Default
                      </div>
                      {insights.filter(ins => {
                        if (!ins.allowedGroups || ins.allowedGroups.length === 0) return true;
                        if (adminMode) return true;
                        return ins.allowedGroups.some(g => userGroups?.includes(g));
                      }).map(ins => (
                        <div
                          key={ins.id}
                          className={`panel-insight-option${panelInsightId === ins.id ? ' active' : ''}`}
                          onClick={() => { setPanelInsightId(ins.id); setShowPanelInsightPicker(false); }}
                        >
                          {panelInsightId === ins.id && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                          {ins.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Related work items, the way Jira lays them out: Subtasks,
                  Child work items (the parent field), then Linked work items
                  grouped by the phrase this item shows — a linked parent
                  included. The parent FIELD sits in the header breadcrumb. */}
              {(() => {
                // A Folder's or an insight's columns, where one applies, as
                // before; otherwise each table's own — the ones this person
                // added with its "+", for this work type.
                const hostCols = effectivePanelColumns && effectivePanelColumns.length ? effectivePanelColumns : null;
                const columnsFor = (section) => hostCols || relatedColumnsFromIds(relatedColIds[section] || [], jiraFields);
                const toRows = (items) => items.map(it => ({ key: it.key, issue: getEnrichedIssue(it.key, it) }));
                const isSub = (it) => !!(getEnrichedIssue(it.key, it)?.fields?.issuetype?.subtask);
                const subtaskSet = new Set(subtasks.map(s => s.key));
                const subtaskRows = toRows(allChildren.filter(c => subtaskSet.has(c.key) || isSub(c)));
                const childRows = toRows(allChildren.filter(c => !subtaskSet.has(c.key) && !isSub(c)));
                const renderMiniCell = (enriched, col) => {
                  const canEdit = isFieldEditable(col.fieldId, col) && col.type !== 'formula';
                  const isInlineEditing = childInlineEdit && childInlineEdit.issueKey === enriched.key && childInlineEdit.fieldId === col.fieldId;
                  return (
                    <div className={`panel-mini-cell${canEdit ? ' panel-mini-cell-editable' : ''}`}
                      onClick={canEdit && !isInlineEditing ? (e) => handlePanelCellClick(e, enriched, col) : undefined}>
                      {isInlineEditing ? (
                        <input className="panel-inline-input" type="text" value={childInlineEdit.value}
                          onChange={(e) => setChildInlineEdit(prev => ({ ...prev, value: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleChildInlineSave(); } if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); handleChildInlineCancel(); } }}
                          onBlur={handleChildInlineSave} autoFocus onClick={(e) => e.stopPropagation()} />
                      ) : (<>{renderPanelCell(enriched, col)}{canEdit && <span className="panel-mini-edit-icon">&#9998;</span>}</>)}
                    </div>
                  );
                };
                // A summary renames on a double-click, in place — the same
                // inline edit a text column uses (handleChildInlineSave).
                const renderSummaryEditor = (key) => {
                  if (!childInlineEdit || childInlineEdit.issueKey !== key || childInlineEdit.fieldId !== 'summary') return null;
                  return (
                    <input className="related-summary-input" type="text" value={childInlineEdit.value} aria-label={`Summary of ${key}`}
                      onChange={(e) => setChildInlineEdit(prev => ({ ...prev, value: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleChildInlineSave(); } if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); handleChildInlineCancel(); } }}
                      onBlur={handleChildInlineSave} autoFocus onFocus={(e) => e.currentTarget.select()} />
                  );
                };
                const tablePropsFor = (section) => {
                  const columns = columnsFor(section);
                  return {
                    columns,
                    columnTracks: miniColumnTracks(columns),
                    renderCell: renderMiniCell,
                    siteUrl,
                    onOpenKey: handleIssueKeyClick,
                    onStatusChanged: (key, status, issue) => handleRelatedRowFields(key, { status }, issue),
                    // The "+" only where the table's columns are its own.
                    onChooseColumns: hostCols ? null : (anchor) => setRelatedColPicker({ section, anchor }),
                    onRenameSummary: (key, issue) => setChildInlineEdit({ issueKey: key, fieldId: 'summary', value: issue?.fields?.summary || '', original: issue?.fields?.summary || '' }),
                    renderSummaryEditor,
                  };
                };
                const linkGroups = otherLinks.map(group => ({
                  label: group.label,
                  rows: group.issues.map(({ linkId, issue }) => ({ key: issue.key, linkId, issue: getEnrichedIssue(issue.key, issue) })),
                }));
                const linkRows = linkGroups.flatMap(g => g.rows);
                // ••• — Bulk edit opens Bulk Edit on the section's rows; View
                // in search opens exactly those rows in Zebra Search, in a
                // new app tab, to edit them in the grid.
                const sectionMenu = (title) => ({
                  onBulkEdit: canBulkEdit ? (rows) => setRelatedBulkKeys([...new Set(rows.map(r => r.key))]) : null,
                  onViewInSearch: (rows) => {
                    const keys = [...new Set(rows.map(r => r.key))];
                    if (keys.length) openTab(zebraSearchHref(`key in (${keys.join(', ')})`), `${title} of ${issueKey}`);
                  },
                });
                const canAddLink = !!creationTree && relationshipOptions.length > 0;
                const showParentRow = editingParent || parentSaving || parentSuccessMsg || (!hasParent && canSetParent);
                return (
                  <>
                    {subtaskRows.length > 0 && (
                      <RelatedSection title="Subtasks" kind="subtasks" rows={subtaskRows} {...sectionMenu('Subtasks')}>
                        {(visible) => <RelatedTable rows={visible(subtaskRows)} {...tablePropsFor('subtasks')} />}
                      </RelatedSection>
                    )}
                    {childRows.length > 0 && (
                      <RelatedSection title="Child work items" kind="children" rows={childRows} {...sectionMenu('Child work items')}>
                        {(visible) => <RelatedTable rows={visible(childRows)} {...tablePropsFor('children')} />}
                      </RelatedSection>
                    )}
                    {(otherLinks.length > 0 || canAddLink || showParentRow) && (
                      <RelatedSection
                        title="Linked work items"
                        kind="links"
                        rows={linkRows}
                        {...sectionMenu('Linked work items')}
                        actions={canAddLink && !addingRelationship ? (
                          <>
                      <button
                        type="button"
                        className="add-child-trigger"
                        title="Add a link to another work item"
                        onClick={() => {
                          setAddingChildError(null);
                          if (relationshipGroups.length === 1) {
                            setAddingRelationship({ step: 'pickIssue', group: relationshipGroups[0], query: '' });
                          } else {
                            setAddingRelationship({ step: 'pickRelationship', group: null, query: '' });
                          }
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      </button>
                          </>
                        ) : null}
                      >
                        {(visible) => (<>
                  {/* Inline add-relationship panel — shown when + is clicked. */}
                  {addingRelationship && (
                    <div className="add-child-panel">
                      <div className="add-child-panel-header">
                        <span className="add-child-panel-title">
                          {addingRelationship.step === 'pickRelationship' ? 'Choose relationship' : 'Choose work item'}
                        </span>
                        <button
                          type="button"
                          className="add-child-panel-close"
                          onClick={() => { setAddingRelationship(null); setAddingChildError(null); }}
                          title="Cancel"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>

                      {addingRelationship.step === 'pickRelationship' && (
                        <div className="add-child-rel-list">
                          {relationshipGroups.map(group => (
                            <button
                              key={group.key}
                              type="button"
                              className="add-child-rel-card"
                              onClick={() => setAddingRelationship({ step: 'pickIssue', group, query: '' })}
                            >
                              <span className="add-child-rel-card-head">
                                <span className="add-child-rel-card-type">{group.label}</span>
                                {group.relationshipType === 'linked' && (
                                  <span className="add-child-rel-card-badge add-child-rel-card-badge--linked">{group.linkTypeName}</span>
                                )}
                              </span>
                              <span className="add-child-rel-card-types">
                                {group.typeNames.map(name => {
                                  const icon = relTypeIconByName?.get(name.toLowerCase());
                                  return (
                                    <span key={name} className="add-child-rel-card-typechip">
                                      {icon && <img src={api.getJiraImageUrl(icon)} alt="" width="14" height="14" />}
                                      {name}
                                    </span>
                                  );
                                })}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}

                      {addingRelationship.step === 'pickIssue' && addingRelationship.group && (
                        <>
                          <div className="add-child-selected-rel">
                            <span className="add-child-selected-rel-type">{addingRelationship.group.label}</span>
                            {addingRelationship.group.relationshipType === 'linked' && (
                              <span className="add-child-rel-card-badge add-child-rel-card-badge--linked">{addingRelationship.group.linkTypeName}</span>
                            )}
                            {relationshipGroups.length > 1 && (
                              <button
                                type="button"
                                className="add-child-selected-rel-change"
                                onClick={() => setAddingRelationship(prev => ({ ...prev, step: 'pickRelationship', group: null }))}
                              >Change</button>
                            )}
                          </div>
                          <div className="add-child-picker-row">
                            <IssuePickerInput
                              value={addingRelationship.query}
                              onChange={(e) => setAddingRelationship(prev => ({ ...prev, query: e.target.value }))}
                              onSelect={(key, picked) => handleAddRelationshipSubmit(optionForPicked(addingRelationship.group, picked), key, picked)}
                              currentIssueKey={issueKey}
                              // Subtask / native-child relationships are project-bound
                              // in Jira (`fields.parent` requires same project).
                              // Linked relationships have no such restriction, so the
                              // picker is unscoped → user can attach a candidate from
                              // any project they have access to.
                              project={
                                addingRelationship.group.relationshipType === 'subtask' ||
                                addingRelationship.group.relationshipType === 'child'
                                  ? issueData?.fields?.project?.key
                                  : undefined
                              }
                              issueTypes={addingRelationship.group.typeNames.join(',')}
                              currentJQL={addingRelationship.group.jql}
                              expression={addingRelationship.group.expression}
                              anchorKey={issueKey}
                              candidateRole={addingRelationship.group.role}
                              excludeKeys={(() => {
                                const keys = new Set();
                                for (const s of (mergedIssueData?.fields?.subtasks || [])) {
                                  if (s?.key) keys.add(s.key);
                                }
                                for (const l of (mergedIssueData?.fields?.issuelinks || [])) {
                                  if (l?.inwardIssue?.key) keys.add(l.inwardIssue.key);
                                  if (l?.outwardIssue?.key) keys.add(l.outwardIssue.key);
                                }
                                for (const c of (fetchedChildren || [])) {
                                  if (c?.key) keys.add(c.key);
                                }
                                for (const k of (hierarchyDescendantKeys || [])) {
                                  keys.add(k);
                                }
                                // For 'parent' role, also exclude the current parent (already set)
                                if (addingRelationship.group.role === 'parent' && effectiveParent?.key) {
                                  keys.add(effectiveParent.key);
                                }
                                return Array.from(keys);
                              })()}
                              disabled={addingChildSubmitting}
                              placeholder={addingRelationship.group.typeNames.length === 1
                                ? `Search ${addingRelationship.group.typeNames[0]} work items…`
                                : 'Search work items by key or summary…'}
                              autoFocus
                            />
                            {addingChildSubmitting && <span className="screen-spinner-sm" />}
                          </div>
                          {addingChildError && (
                            <div className="add-child-error">{addingChildError}</div>
                          )}
                        </>
                      )}
                    </div>
                  )}


                        {/* Setting a parent (none yet, or ✎ in the header) */}
                        {showParentRow && (
                          <div className="link-group" ref={parentEditRef}>
                            <div className="link-group-label link-group-label--phrase">Parent</div>
                            {!editingParent && !hasParent && !parentSaving && (
                              <button className="screen-panel-add-link-btn" onClick={() => setEditingParent(true)}>+ Set parent</button>
                            )}
                    {editingParent && (
                      <div style={{ marginTop: 4 }}>
                        <ParentEditor
                          value={effectiveParent?.key || null}
                          disabled={parentSaving}
                          onAutoSave={handleParentSave}
                          {...parentEditorProps}
                        />
                        <button className="screen-panel-cancel-btn" onClick={() => { setEditingParent(false); setParentError(null); }}>Cancel</button>
                        {parentError && <div className="screen-field-error">{parentError}</div>}
                      </div>
                    )}
                    {parentSaving && <div className="parent-loading-msg">Saving parent...</div>}
                    {parentSuccessMsg && !parentSaving && <div className="parent-success-msg">{parentSuccessMsg}</div>}
                          </div>
                        )}

                        {linkGroups.map(group => {
                          const shown = visible(group.rows);
                          return shown.length === 0 ? null : (
                            <div key={group.label} className="link-group">
                              <div className="link-group-label link-group-label--phrase">{group.label}</div>
                              <RelatedTable rows={shown} {...tablePropsFor('links')} onUnlink={handleUnlinkIssue} />
                            </div>
                          );
                        })}
                        </>)}
                      </RelatedSection>
                    )}
                  </>
                );
              })()}

              {/* Time Tracking */}
              {timeTracking && (timeTracking.originalEstimate || timeTracking.timeSpent) && (
                <div className="detail-section">
                  <div className="detail-section-header">Time Tracking</div>
                  <div className="time-tracking-display">
                    <div className="time-tracking-bar">
                      <div className="time-tracking-bar-fill" style={{ width: `${ttPct}%` }} />
                    </div>
                    <div className="time-tracking-stats">
                      <span>Logged: {timeTracking.timeSpent || 'None'}</span>
                      <span>Remaining: {timeTracking.remainingEstimate || 'None'}</span>
                      <span>Original: {timeTracking.originalEstimate || 'None'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Watches & Votes */}
              {(watchCount > 0 || voteCount > 0) && (
                <div className="detail-meta-bar">
                  {watchCount > 0 && <span className="detail-meta-item">{watchCount} watcher{watchCount !== 1 ? 's' : ''}</span>}
                  {voteCount > 0 && <span className="detail-meta-item">{voteCount} vote{voteCount !== 1 ? 's' : ''}</span>}
                </div>
              )}
            </>
          );
        })()}

        {/* Attachments section — always visible */}
        {!loading && !error && (
          <div className="detail-section">
            <div className="detail-section-header detail-section-header--with-action">
              <span>Attachments</span>
              {attachments.length > 0 && (
                <span className="attachments-section-count">{attachments.length}</span>
              )}
              {/* Download all: every file on the work item in one zip, under the
                  same read gate as each file (attachment-handler
                  handleStrideDownloadAllAttachments). Shown from two files up —
                  one file has its own Download on the card. */}
              {attachments.length > 1 && (
                <a
                  className="attachments-download-all"
                  href={(() => { const t = localStorage.getItem('token'); const u = api.getAttachmentsZipUrl(issueKey); return t ? `${u}?token=${encodeURIComponent(t)}` : u; })()}
                  download={`${issueKey}-attachments.zip`}
                  title={`Download all ${attachments.length} attachments of ${issueKey} as one zip`}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v8m0 0L4.8 6.8M8 10l3.2-3.2M2.5 12.5v1h11v-1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Download all
                </a>
              )}
            </div>
            {renderAttachmentsSection()}
          </div>
        )}

        {/* Activity section */}
        {!loading && !error && (
          <div className="activity-section">
              <div className="activity-header">Activity</div>
            <div className="activity-tab-bar">
              <button
                className={`activity-tab ${activityTab === 'comments' ? 'active' : ''}`}
                onClick={() => setActivityTab('comments')}
              >
                Comments
                {comments && <span className="activity-tab-count">{comments.length}</span>}
              </button>
              <button
                className={`activity-tab ${activityTab === 'history' ? 'active' : ''}`}
                onClick={() => setActivityTab('history')}
              >
                History
              </button>
            </div>
            <div className="activity-content">
              {activityTab === 'comments' && renderCommentsTab()}
              {activityTab === 'history' && renderHistoryTab()}
            </div>
          </div>
        )}
      </div>

      {/* Bulk edit — a related section's rows */}
      {relatedBulkKeys && (
        <BulkChangeModal
          open
          onClose={() => setRelatedBulkKeys(null)}
          selectedKeys={relatedBulkKeys}
          itemsByKey={new Map(relatedBulkKeys.map(k => [k, getEnrichedIssue(k, { key: k })]))}
          jiraFields={jiraFields}
          siteUrl={siteUrl}
          onComplete={(res) => { if (res && res.processed > 0) setRelatedRefresh(n => n + 1); }}
        />
      )}

      {/* Transition Screen Modal — opened when a transition has a screen */}
      {transitionScreenFor && (
        <TransitionScreenModal
          issueKey={issueKey}
          transition={transitionScreenFor}
          onClose={() => setTransitionScreenFor(null)}
          onSubmit={async (fields, comment) => {
            const res = await handleTransition(
              transitionScreenFor.id,
              transitionScreenFor.to?.name || transitionScreenFor.name,
              fields,
              comment,
            );
            if (res && res.ok) setTransitionScreenFor(null);
            else throw (res || { error: 'Transition failed.' });
          }}
        />
      )}
      {/* Create Issue Modal */}
      <CreateIssueModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleModalCreated}
        mode={createModalMode}
        projectKey={mergedIssueData?.fields?.project?.key}
        projectId={mergedIssueData?.fields?.project?.id}
        parentIssueKey={issueKey}
        linkInfo={createModalLinkInfo}
        hierarchy={hierarchyCreate}
      />

      {/* FieldEditor for child/linked item inline editing */}
      {childEditingField && (
        <FieldEditor
          issueKey={childEditingField.issueKey}
          fieldId={childEditingField.fieldId}
          editorType={childEditingField.editorType}
          currentValue={childEditingField.currentValue}
          anchorRect={childEditingField.anchorRect}
          issueData={childEditingField.issueData}
          jiraFields={jiraFields}
          columns={effectivePanelColumns}
          onSave={handlePanelFieldSave}
          onClose={handlePanelFieldClose}
        />
      )}

      {/* The columns picker under a related-items table's "+" */}
      {relatedColPicker && (
        <RelatedColumnPicker
          anchor={relatedColPicker.anchor}
          catalogue={relatedFieldCatalogue(jiraFields)}
          chosenIds={relatedColIds[relatedColPicker.section] || []}
          onToggle={(fieldId) => toggleRelatedColumn(relatedColPicker.section, fieldId)}
          onReset={() => resetRelatedColumns(relatedColPicker.section)}
          onClose={() => setRelatedColPicker(null)}
        />
      )}

      {/* Drag-and-drop overlay */}
      {panelDragActive && (
        <div className="panel-drag-overlay">
          <div className="panel-drag-overlay-content">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            <span>Drop files to attach</span>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!discardConfirm}
        title="Discard changes?"
        message="Your unsaved changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        onConfirm={() => {
          const fn = discardConfirm;
          setDiscardConfirm(null);
          if (typeof fn === 'function') fn();
        }}
        onCancel={() => setDiscardConfirm(null)}
      />

      <ConfirmDialog
        open={!!deletingComment}
        icon="delete"
        title="Delete comment?"
        message={
          deletingComment && !deletingComment.parentId
            && (comments || []).some(c => c.parentId === deletingComment.id)
            ? 'This comment and every reply on it will be permanently deleted.'
            : 'This comment will be permanently deleted.'
        }
        confirmLabel={deletingCommentBusy ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => { if (!deletingCommentBusy) handleDeleteComment(deletingComment); }}
        onCancel={() => { if (!deletingCommentBusy) setDeletingComment(null); }}
      />

    </div>
  );
}

export default IssueScreenPanel;
